import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Operacao, Documento, Mensagem, Tarefa, PropostaBancaria, EventoOperacao, CriticaCCV, ChecklistItem, EtapaExigencia, Participante, PapelParticipante, Programa, DivergenciaConector, PendenciaInformacao, Ocupacao, StatusPropostaBancaria, MatrizTransicaoProposta, MovimentacaoProposta, SistemaAmortizacao, ItemFilaConsultaCadastral, DocumentoSicaqSemExtracao, FilaConsultaCadastralResposta } from '../types';
import { ESTEIRAS_CONFIG } from '../types';
import { operacoesApi, propostasApi, partesApi, isolveApi, divergenciasApi, ocupacoesApi, type ParteDetalhe, type ErroISolve, type ItemDiffRecoleta } from '../services/api';
import { toastErroISolve } from '../utils/toastErroISolve';
import EnviarWhatsAppModal from './EnviarWhatsAppModal';
import { toast } from 'sonner';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { Sheet, SheetContent } from './ui/sheet';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import DivergenciasMini from './DivergenciasMini';
import SimulacaoCaixaCard from './SimulacaoCaixaCard';
import DropdownAcoes from './DropdownAcoes';
import OperacaoComentarios from './OperacaoComentarios';
import {
  Search, ScanSearch, Copy, Check, RotateCw, Snowflake, AlertTriangle,
  RefreshCw, FileText, CheckCircle2, PenLine, Download, Circle,
  User, Building2, Save, XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface OperacaoModalProps {
  operacao: Operacao;
  onClose: () => void;
  onUpdate: (op: Operacao) => void;
  // Aba aberta ao montar (ex.: clique numa notificação de menção abre "comentarios")
  abaInicial?: string;
}

function formatDoc(doc: string): string {
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
}

function formatMoeda(valor: number | null | undefined): string {
  if (valor == null || valor === 0) return '-';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatData(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatPct(taxa: number | undefined): string {
  if (taxa == null) return '-';
  return (taxa * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '% a.a.';
}

// X2 (15/07/2026, E5-bis) - rota profunda da Pesquisa Cadastral Simplificada (SICAQ).
// SEM CPF na URL de propósito: (1) sem evidência de que a tela aceite CPF por
// querystring; (2) CPF em URL vazaria pro histórico do navegador e logs de proxy -
// violaria a doutrina de PII por um caminho diferente do já coberto em log de
// servidor. O botão copia o CPF pro clipboard; o humano cola no campo da tela.
// Comportamento ACEITO (não é erro do CRM): sem sessão SICAQ ativa, a CAIXA redireciona
// para o login - a extensão de navegador (Próximas portas, EST-049) resolve isso mais
// à frente; por ora o gesto humano de logar/resolver CAPTCHA continua fora do CRM.
// X2-fix (15/07/2026) - URL original (`consulta_cadastral_captcha_gerar_novo`) era a
// AÇÃO de regenerar CAPTCHA (estado-dependente), não a página de entrada - deu
// "Operação não Realizada" no teste a frio do Emerson. `consulta_cadastral1` é a tela
// real, testada a frio (deep-link direto) e com sessão ativa.
const URL_SICAQ_CONSULTA_CADASTRAL = 'https://caixaaqui.caixa.gov.br/caixaaqui/CaixaAquiController/consulta_cadastral/consulta_cadastral1';

// SPEC S1 (E4) - data relativa para o feed de eventos ("há 2 dias", "agora mesmo")
const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
function formatDataRelativa(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  if (Math.abs(diffMin) < 1) return 'agora mesmo';
  if (Math.abs(diffMin) < 60) return RELATIVE_TIME_FORMATTER.format(diffMin, 'minute');
  const diffHoras = Math.round(diffMin / 60);
  if (Math.abs(diffHoras) < 24) return RELATIVE_TIME_FORMATTER.format(diffHoras, 'hour');
  const diffDias = Math.round(diffHoras / 24);
  return RELATIVE_TIME_FORMATTER.format(diffDias, 'day');
}

// SPEC S1 (E4) - feed de eventos: tipo→ícone/rótulo, severidade→cor
const EVENTO_LABEL: Record<string, { rotulo: string; icone: LucideIcon }> = {
  DADOS_ATUALIZADOS:     { rotulo: 'Dados atualizados',     icone: RefreshCw },
  DOCUMENTOS_NOVOS:      { rotulo: 'Documentos novos',      icone: FileText },
  DIVERGENCIA_ABERTA:    { rotulo: 'Divergência aberta',    icone: AlertTriangle },
  DIVERGENCIA_RESOLVIDA: { rotulo: 'Divergência resolvida', icone: CheckCircle2 },
  CCV_ASSINADO:          { rotulo: 'CCV assinado',          icone: PenLine },
  OPERACAO_IMPORTADA:    { rotulo: 'Operação importada',    icone: Download },
  CRITICA_CCV_CONCLUIDA: { rotulo: 'Crítica do CCV',        icone: ScanSearch },
};

// SPEC S3 (E4) - legenda da categoria de comparação campo a campo
const CATEGORIA_CRITICA_LABEL: Record<string, { icone: string; cor: string }> = {
  ok:              { icone: '✓', cor: 'text-emerald-600' },
  divergente:      { icone: '⚠', cor: 'text-red-600' },
  nao_cadastrado:  { icone: '○', cor: 'text-slate-400' },
  baixa_confianca: { icone: '~', cor: 'text-amber-500' },
};

const EVENTO_COR: Record<string, string> = {
  INFO:      'bg-blue-50 border-blue-200 text-blue-700',
  ACIONAVEL: 'bg-amber-50 border-amber-200 text-amber-700',
};

// ── Seção com título ────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2">{title}</p>
      {children}
    </section>
  );
}

// ── Card de dado (par label/valor) - estilo "campo" da referência ────────────
// Padrão "Novo Vistoriador": label acima (slate-500, caixa normal) e o valor
// dentro de uma caixa branca com borda leve, como um input em modo leitura.
function DataCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <div className="min-h-[2.375rem] flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800">
        {value}
      </div>
    </div>
  );
}

// ── Formulário no padrão "Novo Vistoriador" ─────────────────────────────────
// Campo = label (com asterisco opcional) + controle + texto de ajuda opcional.
// INPUT_CLS é o estilo-base compartilhado por input/select/textarea do drawer.
const INPUT_CLS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 ' +
  'placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-otm-navy/30 ' +
  'focus:border-otm-navy transition-colors';

function CampoForm({
  label, obrigatorio, ajuda, children, className,
}: {
  label: string;
  obrigatorio?: boolean;
  ajuda?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}{obrigatorio && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {ajuda && <p className="text-xs text-slate-400 mt-1">{ajuda}</p>}
    </div>
  );
}

// ── SPEC_D (E6) - Checklist D agrupado por etapa_exigencia, com farol e badge ──
const ETAPA_LABEL: Record<EtapaExigencia, string> = {
  CREDITO: 'Crédito',
  PRE_ANALISE_JURIDICA: 'Pré-análise jurídica',
  AVALIACAO: 'Avaliação',
  CONTRATACAO: 'Contratação',
  REGISTRO: 'Registro',
};
const ETAPA_ORDEM: EtapaExigencia[] = ['CREDITO', 'PRE_ANALISE_JURIDICA', 'AVALIACAO', 'CONTRATACAO', 'REGISTRO'];

// SPEC_LAYOUT_UX_V1 (E5) - mesma lista de NovaOperacaoModal.tsx (ESTADOS_CIVIS),
// duplicada aqui de propósito: componentes independentes, sem import cruzado
// (mesmo padrão já usado no projeto para mapas pequenos, ex. ESTEIRA_PARA_N).
const ESTADOS_CIVIS_PENDENCIA: { value: string; label: string }[] = [
  { value: 'SOLTEIRO', label: 'Solteiro(a)' },
  { value: 'CASADO', label: 'Casado(a)' },
  { value: 'UNIAO_ESTAVEL', label: 'União estável' },
  { value: 'DIVORCIADO', label: 'Divorciado(a)' },
  { value: 'DIVORCIADO_SEM_PARTILHA', label: 'Divorciado(a) sem partilha' },
  { value: 'SEPARADO_FATO', label: 'Separado(a) de fato' },
  { value: 'VIUVO', label: 'Viúvo(a)' },
  { value: 'VIUVO_INVENTARIO_ABERTO', label: 'Viúvo(a) - inventário aberto' },
];

// SPEC_CHECKLIST_INTELIGENTE_V1_1 (UPDATE de dado, 07/07/2026) - vocabulário
// TipoContratacaoCurado (mesmo do dropdown "tipo de contratação" do iSolve).
// APOSENTADO/PROFISSIONAL_LIBERAL existem no enum mas nenhum código raw
// observado ainda mapeia pra eles - ficam disponíveis pra curadoria manual
// mesmo assim (pendência é resolvida por escolha humana, não só automática).
const TIPOS_CONTRATACAO_PENDENCIA: { value: string; label: string }[] = [
  { value: 'ASSALARIADO', label: 'Assalariado' },
  { value: 'AUTONOMO', label: 'Autônomo' },
  { value: 'SOCIO_PROPRIETARIO', label: 'Sócio Proprietário' },
  { value: 'APOSENTADO', label: 'Aposentado' },
  { value: 'PROFISSIONAL_LIBERAL', label: 'Profissional Liberal' },
];

// SPEC_CHECKLIST_INTELIGENTE_V1_1 (E2, 07/07/2026) - balde D2: 12 campos
// booleanos simples (Sim/Não) compartilham o mesmo widget de resolução de
// pendência que `usoFGTS` já usava; os 3 campos de enum (modalidade de
// aquisição, regime de bens desejado, causa de perda de residência) têm
// select próprio logo abaixo, um por vez.
const CAMPOS_BOOLEAN_PENDENCIA = [
  'desistenciaFgts', 'emprestimoSaqueAniversario', 'transferenciaDividaFgts',
  'residenteExterior', 'vinculoCca3Grau', 'incapazCuratela', 'guardaTutelaMenor',
  'parentescoCoabitacao', 'participantesTerceirosCoabitacao', 'holdingPatrimonial',
  'origemContingencialBeneficio', 'representadoPorProcuracao',
];

const REGIMES_BENS_PENDENCIA: { value: string; label: string }[] = [
  { value: 'COMUNHAO_PARCIAL', label: 'Comunhão parcial de bens' },
  { value: 'COMUNHAO_UNIVERSAL', label: 'Comunhão universal de bens' },
  { value: 'SEPARACAO_CONVENCIONAL', label: 'Separação convencional de bens' },
  { value: 'SEPARACAO_OBRIGATORIA', label: 'Separação obrigatória de bens' },
  { value: 'PARTICIPACAO_FINAL_AQUESTOS', label: 'Participação final nos aquestos' },
  { value: 'NAO_APLICAVEL', label: 'Não aplicável' },
];

const CHECKLIST_STATUS_BADGE: Record<string, string> = {
  PENDENTE: 'bg-slate-100 text-slate-600',
  ENTREGUE: 'bg-emerald-100 text-emerald-700',
  VENCIDO: 'bg-red-100 text-red-600',
  AUSENTE: 'bg-amber-100 text-amber-700',
  NAO_APLICAVEL: 'bg-slate-50 text-slate-300',
};

// SPEC_CHECKLIST_INTELIGENTE_V1_1 (E3, 07/07/2026) - balde B: itens
// categoria=CHECAGEM_EXTERNA (SERASA/SCPC, CADIN, SCR, tabela FGTS municípios,
// alçada de comitê, histórico de subsídio) viram grupo próprio com resultado
// registrável, fora do agrupamento por etapa (ver split em itensExibidos).
const VEREDITO_LABEL: Record<string, string> = { OK: 'OK', RESTRICAO: 'Restrição', OBSERVACAO: 'Observação' };
const VEREDITO_BADGE: Record<string, string> = {
  OK: 'bg-emerald-100 text-emerald-700',
  RESTRICAO: 'bg-red-100 text-red-600',
  OBSERVACAO: 'bg-amber-100 text-amber-700',
};

function ChecagensExternas({ itens, operacaoId, onRegistrado }: { itens: ChecklistItem[]; operacaoId: string; onRegistrado: () => void }) {
  const [vereditoPorItem, setVereditoPorItem] = useState<Record<string, string>>({});
  const [observacaoPorItem, setObservacaoPorItem] = useState<Record<string, string>>({});
  const [registrandoId, setRegistrandoId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [expandidoId, setExpandidoId] = useState<string | null>(null);

  if (itens.length === 0) return null;

  const registrar = async (item: ChecklistItem) => {
    const veredito = vereditoPorItem[item.id];
    if (!veredito) { setErro('Selecione um veredito antes de registrar.'); return; }
    setRegistrandoId(item.id);
    setErro(null);
    try {
      await operacoesApi.registrarChecagemExterna(operacaoId, item.id, {
        veredito: veredito as 'OK' | 'RESTRICAO' | 'OBSERVACAO',
        observacao: observacaoPorItem[item.id]?.trim() || undefined,
      });
      setVereditoPorItem((s) => ({ ...s, [item.id]: '' }));
      setObservacaoPorItem((s) => ({ ...s, [item.id]: '' }));
      onRegistrado();
    } catch {
      setErro('Erro ao registrar a checagem - tente novamente.');
    } finally {
      setRegistrandoId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-2 h-2 rounded-full bg-indigo-400" />
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Checagens externas</p>
        <span className="text-[10px] text-slate-400">({itens.length})</span>
      </div>
      <div className="space-y-2">
        {itens.map((item) => {
          const historico = item.checagensExternas ?? [];
          const ultima = historico[0];
          return (
            <div key={item.id} className="border border-slate-100 rounded-lg px-3 py-2 bg-slate-50/50">
              <div className="flex items-start gap-2 text-sm">
                <span className="flex-1 text-slate-700 leading-snug">
                  <span className="font-mono text-[10px] text-slate-400 mr-1">{item.regra.codigo}</span>
                  {item.regra.descricaoExigencia}
                </span>
                {ultima ? (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${VEREDITO_BADGE[ultima.veredito]}`}>
                    {VEREDITO_LABEL[ultima.veredito]}
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 bg-slate-100 text-slate-400">Não checado</span>
                )}
              </div>
              {ultima && (
                <p className="text-[11px] text-slate-400 mt-1">
                  {ultima.usuario.nome} em {new Date(ultima.checadoEm).toLocaleDateString('pt-BR')}
                  {ultima.observacao ? ` - ${ultima.observacao}` : ''}
                  {historico.length > 1 && (
                    <button type="button" onClick={() => setExpandidoId((id) => (id === item.id ? null : item.id))} className="ml-1.5 text-blue-600 hover:underline">
                      {expandidoId === item.id ? 'ocultar histórico' : `+${historico.length - 1} anterior(es)`}
                    </button>
                  )}
                </p>
              )}
              {expandidoId === item.id && (
                <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-slate-200">
                  {historico.slice(1).map((c) => (
                    <p key={c.id} className="text-[11px] text-slate-400">
                      {VEREDITO_LABEL[c.veredito]} - {c.usuario.nome} em {new Date(c.checadoEm).toLocaleDateString('pt-BR')}{c.observacao ? ` - ${c.observacao}` : ''}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-1.5 mt-1.5">
                <select
                  value={vereditoPorItem[item.id] ?? ''}
                  onChange={(e) => setVereditoPorItem((s) => ({ ...s, [item.id]: e.target.value }))}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1"
                >
                  <option value="">Registrar…</option>
                  <option value="OK">OK</option>
                  <option value="RESTRICAO">Restrição</option>
                  <option value="OBSERVACAO">Observação</option>
                </select>
                <input
                  type="text"
                  placeholder="Observação (opcional)"
                  value={observacaoPorItem[item.id] ?? ''}
                  onChange={(e) => setObservacaoPorItem((s) => ({ ...s, [item.id]: e.target.value }))}
                  className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1"
                />
                <button
                  type="button"
                  onClick={() => registrar(item)}
                  disabled={registrandoId === item.id}
                  className="text-xs font-medium text-white bg-slate-700 px-2.5 py-1 rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors shrink-0"
                >
                  {registrandoId === item.id ? 'Salvando…' : 'Registrar'}
                </button>
              </div>
            </div>
          );
        })}
        {erro && <p className="text-xs text-red-500">{erro}</p>}
      </div>
    </div>
  );
}

function ChecklistPorEtapa({
  itens, mostrarNaoAplicavel, onToggleNaoAplicavel,
}: {
  itens: ChecklistItem[];
  mostrarNaoAplicavel: boolean;
  onToggleNaoAplicavel: () => void;
}) {
  // SPEC_LAYOUT_UX_V1 (E5.2, lacuna fechada em 07/07/2026 na
  // SPEC_CHECKLIST_INTELIGENTE_V1_1 E4) - fases futuras (faseAlcancada=false)
  // começam colapsadas num contador; a fase atual continua expandida como
  // sempre. v1 mínima: sem refinamento visual, expand/collapse por etapa.
  const [etapasExpandidas, setEtapasExpandidas] = useState<Set<EtapaExigencia>>(new Set());
  const toggleEtapa = (etapa: EtapaExigencia) => setEtapasExpandidas((prev) => {
    const novo = new Set(prev);
    if (novo.has(etapa)) novo.delete(etapa); else novo.add(etapa);
    return novo;
  });

  const porEtapa = new Map<EtapaExigencia, ChecklistItem[]>();
  for (const item of itens) {
    if (item.status === 'NAO_APLICAVEL' && !mostrarNaoAplicavel) continue;
    const lista = porEtapa.get(item.regra.etapaExigencia) ?? [];
    lista.push(item);
    porEtapa.set(item.regra.etapaExigencia, lista);
  }
  const naoAplicaveisCount = itens.filter((i) => i.status === 'NAO_APLICAVEL').length;

  return (
    <div className="space-y-4">
      {ETAPA_ORDEM.filter((e) => porEtapa.has(e)).map((etapa) => {
        const lista = porEtapa.get(etapa)!;
        // Todos os itens de uma etapa compartilham o mesmo etapaExigencia,
        // logo o mesmo faseAlcancada - basta olhar o primeiro.
        const faseAtual = lista[0]?.faseAlcancada ?? true;
        const expandida = faseAtual || etapasExpandidas.has(etapa);
        const vencidos = lista.filter((i) => i.status === 'VENCIDO').length;
        const pendentes = lista.filter((i) => i.status === 'PENDENTE' || i.status === 'AUSENTE').length;
        const farol = vencidos > 0 ? 'bg-red-500' : pendentes > 0 ? 'bg-amber-400' : 'bg-emerald-500';
        return (
          <div key={etapa}>
            <button
              type="button"
              onClick={faseAtual ? undefined : () => toggleEtapa(etapa)}
              className={`flex items-center gap-2 mb-1.5 w-full text-left ${faseAtual ? 'cursor-default' : 'cursor-pointer hover:opacity-70'}`}
            >
              {!faseAtual && <span className="text-[10px] text-slate-400">{expandida ? '▾' : '▸'}</span>}
              <span className={`w-2 h-2 rounded-full ${farol}`} />
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">{ETAPA_LABEL[etapa]}</p>
              <span className="text-[10px] text-slate-400">({lista.length} {faseAtual ? '' : 'itens - fase futura'})</span>
            </button>
            {expandida && (
              <div className="space-y-1">
                {lista.map((item) => (
                  <div key={item.id} className={`flex items-start gap-2 text-sm px-2.5 py-1.5 rounded-lg ${item.status === 'NAO_APLICAVEL' ? 'opacity-50' : ''}`}>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${CHECKLIST_STATUS_BADGE[item.status]}`}>{item.status}</span>
                    <span className="flex-1 text-slate-700 leading-snug">
                      <span className="font-mono text-[10px] text-slate-400 mr-1">{item.regra.codigo}</span>
                      {item.regra.descricaoExigencia}
                      {item.status === 'ENTREGUE' && item.documento && (
                        <a href={item.documento.urlSharePoint ?? '#'} target="_blank" rel="noreferrer" className="ml-1.5 text-blue-600 hover:underline text-xs">
                          ↗ {item.documento.nome}
                        </a>
                      )}
                      {item.detalhe?.nota && (
                        <span className="block text-[11px] text-amber-600 mt-0.5">⚠ {item.detalhe.nota}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {naoAplicaveisCount > 0 && (
        <button type="button" onClick={onToggleNaoAplicavel} className="text-xs text-slate-400 hover:text-slate-600">
          {mostrarNaoAplicavel ? '▾' : '▸'} {naoAplicaveisCount} item(ns) não aplicável(is) {mostrarNaoAplicavel ? '(ocultar)' : '(mostrar)'}
        </button>
      )}
    </div>
  );
}

// SPEC_D (E4/E6) - chip "provisório/definitivo" dos motores em dois tempos.
// null = ainda não apurado; true/false renderiza valor + destaca divergência.
function DoisTemposChip({ label, provisorio, definitivo }: { label: string; provisorio?: boolean | null; definitivo?: boolean | null }) {
  if (provisorio == null && definitivo == null) return null;
  const divergente = provisorio != null && definitivo != null && provisorio !== definitivo;
  const fmt = (v?: boolean | null) => (v == null ? '?' : v ? 'sim' : 'não');
  return (
    <span className={`text-[10px] font-medium px-2 py-1 rounded-full border ${divergente ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      {label}: prov. {fmt(provisorio)} / def. {fmt(definitivo)}{divergente ? ' ⚠' : ''}
    </span>
  );
}

const STATUS_BADGE: Record<string, string> = {
  ATIVA:     'bg-emerald-100 text-emerald-700 border-emerald-200',
  CONGELADA: 'bg-slate-200 text-slate-600 border-slate-300',
  CONCLUIDA: 'bg-blue-100 text-blue-700 border-blue-200',
  CANCELADA: 'bg-red-100 text-red-600 border-red-200',
};

// SPEC S2, ajuste pós-Fase-1 (03/07/2026, INT-043) - rótulos humanos + legenda para a
// aba Docs (é a tela onde o Emerson revisa o GATE E6). RECEBIDO_AGUARDA_VALIDACAO é
// status COMPARTILHADO entre a ingestão iSolve (função ativa hoje) e o rename real da
// triagem (função ainda inativa - GATE E6 fechado); origemIsolve desambigua o rótulo
// para as duas situações nunca se confundirem quando o gate abrir.
const COR_STATUS_DOC: Record<Documento['status'], string> = {
  PENDENTE:                    'bg-slate-100 text-slate-500 border-slate-200',
  PROPOSTA_PENDENTE_APROVACAO: 'bg-amber-100 text-amber-700 border-amber-200',
  TRIAGEM_MANUAL:              'bg-orange-100 text-orange-700 border-orange-200',
  AUSENTE:                     'bg-red-100 text-red-600 border-red-200',
  RECEBIDO_AGUARDA_VALIDACAO:  'bg-blue-100 text-blue-700 border-blue-200',
  VALIDADO:                    'bg-emerald-100 text-emerald-700 border-emerald-200',
  REJEITADO:                   'bg-red-100 text-red-700 border-red-200',
};

const LEGENDA_STATUS_DOC: { status: Documento['status']; rotulo: string }[] = [
  { status: 'PENDENTE',                   rotulo: 'Aguardando triagem' },
  { status: 'PROPOSTA_PENDENTE_APROVACAO', rotulo: 'Classificado - aguardando aprovação' },
  { status: 'TRIAGEM_MANUAL',             rotulo: 'Triagem manual' },
  { status: 'AUSENTE',                    rotulo: 'Sumiu da pasta' },
  { status: 'RECEBIDO_AGUARDA_VALIDACAO', rotulo: 'Aguarda validação' },
  { status: 'VALIDADO',                   rotulo: 'Validado' },
  { status: 'REJEITADO',                  rotulo: 'Rejeitado' },
];

function rotuloStatusDocumento(doc: Documento): string {
  if (doc.status === 'RECEBIDO_AGUARDA_VALIDACAO') {
    return doc.origemIsolve ? 'Recebido do iSolve - aguarda validação' : 'Triado pela IA - aguarda validação';
  }
  return LEGENDA_STATUS_DOC.find((l) => l.status === doc.status)?.rotulo ?? doc.status;
}

const COR_STATUS_PROPOSTA: Record<string, string> = {
  ENVIADA:      'bg-blue-100 text-blue-700 border-blue-200',
  AGUARDANDO:   'bg-amber-100 text-amber-700 border-amber-200',
  APROVADA:     'bg-emerald-100 text-emerald-700 border-emerald-200',
  REPROVADA:    'bg-red-100 text-red-700 border-red-200',
  PENDENTE_DOC: 'bg-orange-100 text-orange-700 border-orange-200',
  CANCELADA:    'bg-slate-100 text-slate-500 border-slate-200',
};

const LABEL_STATUS_PROPOSTA: Record<string, string> = {
  ENVIADA: 'Enviada', AGUARDANDO: 'Aguardando', APROVADA: 'Aprovada',
  REPROVADA: 'Reprovada', PENDENTE_DOC: 'Pend. Doc', CANCELADA: 'Cancelada',
};

// ---------------------------------------------------------------------------
// Drawer da Parte - campos editáveis (enriquecimento iSolve v2)
// ---------------------------------------------------------------------------
type TipoCampoParte = 'text' | 'email' | 'date' | 'number';
interface CampoParte { key: string; label: string; tipo: TipoCampoParte; }
interface SecaoParte { titulo: string; campos: CampoParte[]; }

const SECOES_PARTE: SecaoParte[] = [
  { titulo: 'Identidade', campos: [
    { key: 'sexo', label: 'Sexo', tipo: 'text' },
    { key: 'dataNascimento', label: 'Data de nascimento', tipo: 'date' },
    { key: 'nomeMae', label: 'Nome da mãe', tipo: 'text' },
    { key: 'nomePai', label: 'Nome do pai', tipo: 'text' },
    { key: 'nacionalidade', label: 'Nacionalidade', tipo: 'text' },
    { key: 'naturalidade', label: 'Naturalidade', tipo: 'text' },
    { key: 'ufNascimento', label: 'UF de nascimento', tipo: 'text' },
    { key: 'grauInstrucao', label: 'Grau de instrução', tipo: 'text' },
  ]},
  { titulo: 'Documento', campos: [
    { key: 'rg', label: 'RG', tipo: 'text' },
    { key: 'tipoDocumento', label: 'Tipo de documento', tipo: 'text' },
    { key: 'numeroDocumento', label: 'Número do documento', tipo: 'text' },
    { key: 'orgaoExpedidorDoc', label: 'Órgão expedidor', tipo: 'text' },
    { key: 'dataEmissaoDoc', label: 'Data de emissão', tipo: 'date' },
  ]},
  { titulo: 'Endereço', campos: [
    { key: 'enderecoLogradouro', label: 'Logradouro', tipo: 'text' },
    { key: 'enderecoNumero', label: 'Número', tipo: 'text' },
    { key: 'enderecoComplemento', label: 'Complemento', tipo: 'text' },
    { key: 'enderecoBairro', label: 'Bairro', tipo: 'text' },
    { key: 'enderecoCidade', label: 'Cidade', tipo: 'text' },
    { key: 'enderecoUf', label: 'UF', tipo: 'text' },
    { key: 'enderecoCep', label: 'CEP', tipo: 'text' },
    { key: 'tempoResidencia', label: 'Tempo de residência', tipo: 'text' },
    { key: 'tipoResidencia', label: 'Tipo de residência', tipo: 'text' },
  ]},
  { titulo: 'Profissional e renda', campos: [
    { key: 'profissao', label: 'Profissão', tipo: 'text' },
    { key: 'cargo', label: 'Cargo', tipo: 'text' },
    { key: 'tipoContratacao', label: 'Tipo de contratação', tipo: 'text' },
    { key: 'dataAdmissao', label: 'Data de admissão', tipo: 'date' },
    { key: 'nomeEmpresa', label: 'Empresa', tipo: 'text' },
    { key: 'cnpjEmpresa', label: 'CNPJ da empresa', tipo: 'text' },
    { key: 'rendaMensal', label: 'Renda mensal (R$)', tipo: 'number' },
    { key: 'outraRenda', label: 'Outra renda (R$)', tipo: 'number' },
    { key: 'origemOutraRenda', label: 'Origem da outra renda', tipo: 'text' },
    { key: 'rendaFamiliar', label: 'Renda familiar (R$)', tipo: 'number' },
    { key: 'socioEmpresa', label: 'Sócio de empresa', tipo: 'text' },
    { key: 'cnpjSocioProprietario', label: 'CNPJ sócio/proprietário', tipo: 'text' },
    { key: 'patrimonio', label: 'Patrimônio (R$)', tipo: 'number' },
  ]},
  { titulo: 'Cônjuge e união', campos: [
    { key: 'regimeCasamento', label: 'Regime de casamento', tipo: 'text' },
    { key: 'dataUniaoOuCasamento', label: 'Data de união/casamento', tipo: 'date' },
    { key: 'conjugeNome', label: 'Nome do cônjuge', tipo: 'text' },
    { key: 'conjugeCpf', label: 'CPF do cônjuge', tipo: 'text' },
    { key: 'conjugeEmail', label: 'Email do cônjuge', tipo: 'email' },
  ]},
  { titulo: 'PEP e compliance', campos: [
    { key: 'cpfPep', label: 'CPF PEP', tipo: 'text' },
    { key: 'nomePep', label: 'Nome PEP', tipo: 'text' },
    { key: 'profissaoPep', label: 'Profissão PEP', tipo: 'text' },
    { key: 'paisResidenciaPep', label: 'País de residência PEP', tipo: 'text' },
    { key: 'dsAmericano', label: 'Americano', tipo: 'text' },
  ]},
  { titulo: 'INSS', campos: [
    { key: 'nrInss', label: 'Nº INSS', tipo: 'text' },
    { key: 'rendaInssAnual', label: 'Rendimento anual INSS (R$)', tipo: 'number' },
  ]},
  { titulo: 'Imóvel (pessoa)', campos: [
    { key: 'imovelFinanciadoSfh', label: 'Imóvel financiado SFH', tipo: 'text' },
    { key: 'imovelMunicipioResidente', label: 'Município residente', tipo: 'text' },
    { key: 'imovelOndeExerceOcupacao', label: 'Município onde exerce ocupação', tipo: 'text' },
    { key: 'imovelMunicipioAdquirir', label: 'Município onde adquirir', tipo: 'text' },
  ]},
];

const OPCOES_ESTADO_CIVIL: Array<{ valor: string; rotulo: string }> = [
  { valor: '', rotulo: '-' },
  { valor: 'SOLTEIRO', rotulo: 'Solteiro(a)' },
  { valor: 'CASADO', rotulo: 'Casado(a)' },
  { valor: 'DIVORCIADO', rotulo: 'Divorciado(a)' },
  { valor: 'VIUVO', rotulo: 'Viúvo(a)' },
  { valor: 'UNIAO_ESTAVEL', rotulo: 'União estável' },
  { valor: 'SEPARADO_FATO', rotulo: 'Separado de fato' },
  { valor: 'DIVORCIADO_SEM_PARTILHA', rotulo: 'Divorciado sem partilha' },
  { valor: 'VIUVO_INVENTARIO_ABERTO', rotulo: 'Viúvo com inventário aberto' },
];

const CAMPOS_BOOLEANO_PARTE: Array<{ key: string; label: string }> = [
  { key: 'estrangeiro', label: 'Comprador estrangeiro' },
  { key: 'usaFgts', label: 'Usa FGTS nesta operação' },
  { key: 'uniaoEstavel', label: 'Mantém união estável' },
  { key: 'pessoaPoliticamenteExposta', label: 'Pessoa politicamente exposta' },
  { key: 'correntistaBradesco', label: 'Correntista Bradesco' },
];

export default function OperacaoModal({ operacao: op, onClose, onUpdate, abaInicial }: OperacaoModalProps) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.papel === 'ADMIN';
  const [clienteConduz, setClienteConduz] = useState(op.clienteConduz);
  const [salvando, setSalvando] = useState(false);

  // E4(ii) SPEC B - botão "Trazer documentos do iSolve" (aba Docs, sob demanda)
  const [trazendoDocs, setTrazendoDocs] = useState(false);
  const [erroDocs, setErroDocs]         = useState<string | null>(null);

  // Exclusao definitiva (hard delete; ADMIN) - so na pagina da operacao
  const [mostrarExcluir, setMostrarExcluir]         = useState(false);
  const [confirmacaoExcluir, setConfirmacaoExcluir] = useState('');
  const [excluindo, setExcluindo]                   = useState(false);
  const [erroExcluir, setErroExcluir]               = useState<string | null>(null);

  async function handleExcluir() {
    if (confirmacaoExcluir.trim() !== op.numeroProcesso) {
      setErroExcluir(`Digite exatamente o numero da operacao: ${op.numeroProcesso}`);
      return;
    }
    setExcluindo(true);
    setErroExcluir(null);
    try {
      await operacoesApi.excluir(op.id, confirmacaoExcluir.trim());
      toast.success(`Operacao ${op.numeroProcesso} excluida.`);
      onClose();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro
        ?? (e instanceof Error ? e.message : 'Erro ao excluir a operacao.');
      setErroExcluir(msg);
    } finally {
      setExcluindo(false);
    }
  }

  async function handleTrazerDocumentos() {
    setTrazendoDocs(true);
    setErroDocs(null);
    try {
      const r = await isolveApi.trazerDocumentosOperacao(op.id);
      if (r.gateDesligado) {
        setErroDocs('Importação de documentos desativada (ISOLVE_INGESTAO_DOCUMENTOS_HABILITADA != "true").');
      } else if (r.motivo) {
        setErroDocs(`Não foi possível importar: ${r.motivo}.`);
      } else if (r.ingeridos > 0) {
        setErroDocs(null);
      } else if ((r.falhasDownload ?? 0) > 0) {
        setErroDocs(`${r.falhasDownload} documento(s) encontrado(s) no iSolve, mas o DOWNLOAD falhou. Encontrados: ${r.encontrados ?? '?'}.`);
      } else if ((r.falhasUpload ?? 0) > 0) {
        setErroDocs(`${r.falhasUpload} documento(s) baixado(s), mas o envio ao SharePoint falhou. Encontrados: ${r.encontrados ?? '?'}.`);
      } else if ((r.falhasListagem ?? 0) > 0) {
        setErroDocs(`Falha ao listar documentos no iSolve (${r.falhasListagem} chamada(s) com erro).`);
      } else if ((r.encontrados ?? 0) > 0) {
        setErroDocs(`Documentos já estavam ingeridos (${r.jaExistiam ?? 0} de ${r.encontrados}).`);
      } else {
        setErroDocs('O iSolve não retornou documentos para esta operação.');
      }
      const atualizada = await operacoesApi.getById(op.id);
      onUpdate(atualizada);
    } catch (e: unknown) {
      const erroDiscriminador = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) {
        toastErroISolve(erroDiscriminador, navigate);
      } else {
        setErroDocs('Erro ao trazer documentos do iSolve - tente novamente.');
      }
    } finally {
      setTrazendoDocs(false);
    }
  }

  // SPEC S2 (E2) - vigia da pasta: gatilho (a) dispara em background ao abrir a aba
  // (tela não trava - docs já carregados aparecem na hora); gatilho (c) é o mesmo
  // endpoint, chamado sob demanda pelo botão "Vigiar pasta".
  const [vigiaAutomatico, setVigiaAutomatico] = useState(false);
  const [vigiando, setVigiando]               = useState(false);

  async function handleVigiar() {
    setVigiando(true);
    try {
      await operacoesApi.vigiar(op.id);
      const atualizada = await operacoesApi.getById(op.id);
      onUpdate(atualizada);
      // X3 (15/07/2026, E5-bis) - o gatilho agora também dispara ao abrir a aba/painel
      // de Consultas (não só Docs); reflete o resultado ali sem esperar o usuário sair
      // e reabrir a aba. Refetch é barato e idempotente - sem efeito se a fila ainda
      // não tiver sido carregada nesta sessão do modal.
      operacoesApi.getFilaConsultaCadastral(op.id).then(aplicarRespostaFilaConsultaCadastral).catch(() => { /* painel só não atualiza - não bloqueia nada */ });
    } catch {
      // não bloqueia a UI - o vigia é best-effort; falha fica só no log do servidor
    } finally {
      setVigiando(false);
    }
  }

  // SPEC S3 (E4) - crítica de CCV: carrega a última ao abrir a aba Docs (mesmo gatilho
  // do vigia), botão "Criticar CCV" roda sob demanda.
  const [criticaCcv, setCriticaCcv]           = useState<CriticaCCV | null>(null);
  const [criticaCarregada, setCriticaCarregada] = useState(false);
  const [criticando, setCriticando]           = useState(false);
  const [erroCritica, setErroCritica]         = useState<string | null>(null);

  async function handleCriticarCcv() {
    setCriticando(true);
    setErroCritica(null);
    try {
      await operacoesApi.criticarCcv(op.id);
      const atual = await operacoesApi.getCriticaCcv(op.id);
      setCriticaCcv(atual);
    } catch {
      setErroCritica('Erro ao criticar o CCV - tente novamente.');
    } finally {
      setCriticando(false);
    }
  }

  // X4-a (15/07/2026, E5-bis) - botão "Reprocessar triagem" (aba Docs), só visível em
  // doc.falhaTransporte. Erro nomeado do backend (422, ambiguidade de classificação em
  // vez de falha de transporte) é mostrado ao usuário, não engolido.
  const [reprocessandoId, setReprocessandoId] = useState<string | null>(null);
  const [erroReprocessar, setErroReprocessar] = useState<string | null>(null);

  async function handleReprocessarTriagem(documentoId: string) {
    setReprocessandoId(documentoId);
    setErroReprocessar(null);
    try {
      await operacoesApi.reprocessarTriagem(op.id, documentoId);
      const atualizada = await operacoesApi.getById(op.id);
      onUpdate(atualizada);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro;
      setErroReprocessar(msg ?? 'Erro ao reprocessar a triagem - tente novamente.');
    } finally {
      setReprocessandoId(null);
    }
  }

  // X4-b (15/07/2026, E5-bis) - botão "Reextrair" (fila de Consultas). Erro nomeado do
  // backend (409, já existe ConsultaCadastral; 422, documento não é SICAQ triado).
  const [reextraindoId, setReextraindoId]     = useState<string | null>(null);
  const [erroReextrair, setErroReextrair]     = useState<string | null>(null);

  async function handleReextrairConsultaCadastral(documentoId: string) {
    setReextraindoId(documentoId);
    setErroReextrair(null);
    try {
      await operacoesApi.reextrairConsultaCadastral(op.id, documentoId);
      const atual = await operacoesApi.getFilaConsultaCadastral(op.id);
      aplicarRespostaFilaConsultaCadastral(atual);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro;
      setErroReextrair(msg ?? 'Erro ao reextrair a consulta cadastral - tente novamente.');
    } finally {
      setReextraindoId(null);
    }
  }

  // SPEC_FILA_CONSULTA_CADASTRAL (E5) - fila de consultas SICAQ: carrega ao abrir a
  // aba (mesmo gatilho da Crítica CCV). SEM tela de homologação lado-a-lado nesta v1.
  const [filaConsultaCadastral, setFilaConsultaCadastral] = useState<ItemFilaConsultaCadastral[]>([]);
  // X4-b (15/07/2026, E5-bis) - documentos SICAQ triados sem ConsultaCadastral ainda
  // (sinal do botão "Reextrair"), sinal separado de `filaConsultaCadastral` (que é
  // por PERSONAGEM, não por documento - ver nota de `documentosSemExtracao` no backend).
  const [documentosSicaqSemExtracao, setDocumentosSicaqSemExtracao] = useState<DocumentoSicaqSemExtracao[]>([]);
  const [filaConsultaCarregada, setFilaConsultaCarregada] = useState(false);

  function aplicarRespostaFilaConsultaCadastral(r: FilaConsultaCadastralResposta) {
    setFilaConsultaCadastral(r.itens);
    setDocumentosSicaqSemExtracao(r.documentosSemExtracao);
  }
  const [cpfCopiado, setCpfCopiado] = useState<string | null>(null);

  // X2 (15/07/2026, E5-bis) - copia o CPF E abre a rota profunda do SICAQ numa aba
  // nova. Sem sessão SICAQ ativa no navegador do usuário, a própria CAIXA redireciona
  // pro login - comportamento aceito, não uma falha desta função.
  async function handleCopiarCpf(parteId: string, cpf: string) {
    await navigator.clipboard.writeText(cpf.replace(/\D/g, ''));
    setCpfCopiado(parteId);
    setTimeout(() => setCpfCopiado((atual) => (atual === parteId ? null : atual)), 2000);
    window.open(URL_SICAQ_CONSULTA_CADASTRAL, '_blank', 'noopener,noreferrer');
  }

  // UI pré-demo 05/07/2026 - pendências (divergências PENDENTE) desta operação, todos os
  // tipos. Alimenta o badge do cabeçalho, o bloco "Pendências da operação" (Resumo) e a
  // seção "Divergências desta crítica" (aba Crítica CCV, filtrada por tipo=CRITICA_CCV a
  // partir do mesmo estado - evita uma segunda chamada). Fila geral (/divergencias) intocada.
  const [pendenciasOperacao, setPendenciasOperacao] = useState<DivergenciaConector[]>([]);

  useEffect(() => {
    divergenciasApi.listar({ operacaoId: op.id, status: 'PENDENTE' })
      .then(setPendenciasOperacao)
      .catch(() => { /* badge/bloco só ficam vazios - não bloqueia o modal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id]);

  const removerPendencia = (id: string) => setPendenciasOperacao((prev) => prev.filter((d) => d.id !== id));

  // X1 (15/07/2026, E5-bis) - painel colapsado no Resumo é agora o ponto de entrada
  // PRINCIPAL da fila de Consultas Cadastrais (a aba "Consultas" continua existindo
  // como vista detalhada - decisão do Emerson, menor risco/sem remover nada testado).
  // Carrega a fila já no mount (o painel é visível de cara, não espera o usuário
  // trocar de aba) e dispara o mesmo gatilho do vigia (X3) - mesma idempotência de
  // sempre (documento já triado retorna JA_TRIADO sem efeito). Consequência aceita:
  // como o Resumo é a aba padrão do modal, isso passa a rodar o ciclo real
  // (vigiarPastaOperacao → triagem) toda vez que QUALQUER operação é aberta, não só
  // quando o usuário visita Docs/Consultas explicitamente - mesmo custo de sempre
  // (idempotente pra quem já está triado), só mais cedo.
  useEffect(() => {
    operacoesApi.getFilaConsultaCadastral(op.id)
      .then((r) => { aplicarRespostaFilaConsultaCadastral(r); setFilaConsultaCarregada(true); })
      .catch(() => { /* painel só fica vazio - não bloqueia o Resumo; abrir a aba Consultas tenta de novo (filaConsultaCarregada continua false) */ });
    if (!vigiaAutomatico) {
      setVigiaAutomatico(true);
      handleVigiar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id]);

  const [parteDrawer, setParteDrawer]         = useState<ParteDetalhe | null>(null);
  const [carregandoParte, setCarregandoParte] = useState(false);
  const [formParte, setFormParte]             = useState<Partial<ParteDetalhe>>({});
  const [salvandoParte, setSalvandoParte]     = useState(false);
  const [erroParte, setErroParte]             = useState<string | null>(null);
  const [sucessoParte, setSucessoParte]       = useState(false);

  // Recoleta sob demanda do iSolve (iSolve → CRM) - diff + aprovação humana.
  const [recoletaItens, setRecoletaItens]           = useState<ItemDiffRecoleta[]>([]);
  const [recoletaSelecao, setRecoletaSelecao]       = useState<Set<string>>(new Set());
  const [recoletaCarregando, setRecoletaCarregando] = useState(false);
  const [recoletaAplicando, setRecoletaAplicando]   = useState(false);
  const [recoletaErro, setRecoletaErro]             = useState<string | null>(null);
  const [recoletaAberta, setRecoletaAberta]         = useState(false);

  // Write-back por ação humana: confirmação in-screen + banner de resultado (nada de window.confirm/alert).
  const [refletirPendente, setRefletirPendente] = useState(false);
  const [refletindo, setRefletindo]             = useState(false);
  const [reflexoMensagem, setReflexoMensagem]   = useState<{ tipo: 'sucesso' | 'aviso' | 'erro'; texto: string } | null>(null);

  // Aba Mensagens - modal de envio de WhatsApp (template / novo template / personalizada).
  const [whatsModalOpen, setWhatsModalOpen] = useState(false);

  // SPEC_LAYOUT_UX_V1 (E6) - bloco de contatos do Drawer da Parte (leitura + adição manual)
  const [novoContato, setNovoContato] = useState<{ tipo: 'email' | 'telefone' | 'whatsapp'; valor: string }>({ tipo: 'telefone', valor: '' });
  const [salvandoContato, setSalvandoContato] = useState(false);
  const [erroContato, setErroContato]         = useState<string | null>(null);

  const adicionarContato = async () => {
    if (!parteDrawer || !novoContato.valor.trim()) {
      setErroContato('Informe o valor do contato.');
      return;
    }
    setSalvandoContato(true);
    setErroContato(null);
    try {
      const { contatos } = await partesApi.adicionarContato(parteDrawer.id, novoContato);
      setParteDrawer((p) => (p ? { ...p, contatos } : p));
      setNovoContato({ tipo: 'telefone', valor: '' });
    } catch (e: any) {
      setErroContato(e?.response?.data?.erro ?? 'Erro ao adicionar contato - tente novamente.');
    } finally {
      setSalvandoContato(false);
    }
  };

  const [propostas, setPropostas]             = useState<PropostaBancaria[]>([]);
  const [carregandoPropostas, setCarregando]  = useState(false);
  const [novaPropostaAberta, setNovaProposta] = useState(false);
  const [erroPropostas, setErroPropostas]     = useState<string | null>(null);
  const [abaPropostas, setAbaPropostas]       = useState(false);

  // SPEC_ESTACAO1 (E6) - controle de transição de status por proposta. Matriz
  // vem do backend (GET /matriz-transicao) - nunca hardcode aqui (MET-011).
  const [matrizTransicao, setMatrizTransicao] = useState<MatrizTransicaoProposta | null>(null);
  const [transicaoAbertaId, setTransicaoAbertaId] = useState<string | null>(null);
  const [destinoTransicao, setDestinoTransicao]   = useState<Record<string, StatusPropostaBancaria | ''>>({});
  const [motivoTransicao, setMotivoTransicao]     = useState<Record<string, string>>({});
  const [transicionandoId, setTransicionandoId]   = useState<string | null>(null);
  const [erroTransicao, setErroTransicao]         = useState<Record<string, string>>({});
  // Histórico (E2/E3) - carregado sob demanda ao expandir "Ver histórico".
  const [historicoAbertoId, setHistoricoAbertoId] = useState<string | null>(null);
  const [movimentacoesPorProposta, setMovimentacoesPorProposta] = useState<Record<string, MovimentacaoProposta[]>>({});
  const [carregandoHistoricoId, setCarregandoHistoricoId]       = useState<string | null>(null);

  // SPEC S1 (E4) - feed de eventos na aba Timeline (lazy, mesmo padrão de propostas)
  const [eventos, setEventos]                 = useState<EventoOperacao[]>([]);
  const [carregandoEventos, setCarregandoEventos] = useState(false);
  const [erroEventos, setErroEventos]         = useState<string | null>(null);
  const [abaEventos, setAbaEventos]           = useState(false);

  // SPEC_D (E3/E6) - Checklist D: itens + participantes, lazy ao abrir a aba
  const [checklistItens, setChecklistItens]   = useState<ChecklistItem[]>([]);
  const [carregandoChecklist, setCarregandoChecklist] = useState(false);
  const [erroChecklist, setErroChecklist]     = useState<string | null>(null);
  const [abaChecklist, setAbaChecklist]       = useState(false);
  const [mostrarNaoAplicavel, setMostrarNaoAplicavel] = useState(false);
  const [participantes, setParticipantes]     = useState<Participante[]>([]);
  const [novoParticipante, setNovoParticipante] = useState({ parteId: '', papel: 'COOBRIGADO' as PapelParticipante, percentual: '' });
  const [salvandoParticipante, setSalvandoParticipante] = useState(false);
  const [erroParticipante, setErroParticipante] = useState<string | null>(null);
  const [classificandoProgramaId, setClassificandoProgramaId] = useState<string | null>(null);

  // SPEC_LAYOUT_UX_V1 (E3, 06/07/2026) - fix do bug relatado pelo Emerson na 0747:
  // o bloco Participantes migrou para o Resumo em 05/07 (a11bc11), mas o fetch
  // continuou preso ao clique na aba Checklist (onTabChange) - como o modal abre
  // sempre em "resumo" (Tabs defaultValue), a lista nunca carregava e parecia que
  // "nada salvava". Carrega no mount, mesmo padrão de pendenciasOperacao acima.
  useEffect(() => {
    operacoesApi.getParticipantes(op.id)
      .then(setParticipantes)
      .catch(() => { /* seção só fica vazia - não bloqueia o modal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id]);
  // SPEC_LAYOUT_UX_V1 (E2) - seletor de banco por chips na aba Checklist.
  // null = ainda não escolhido explicitamente (default = CAIXA quando existir).
  const [bancoChecklistSelecionado, setBancoChecklistSelecionado] = useState<string | null>(null);

  // SPEC_LAYOUT_UX_V1 (E5, 06/07/2026) - pendências de informação: bloco
  // acinzentado-agrupado por dado faltante na aba Checklist.
  const [pendencias, setPendencias]           = useState<PendenciaInformacao[]>([]);
  const [ocupacoesPendencia, setOcupacoesPendencia] = useState<Ocupacao[]>([]);
  const [valorPendencia, setValorPendencia]   = useState<Record<string, string>>({});
  const [resolvendoPendenciaId, setResolvendoPendenciaId] = useState<string | null>(null);
  const [erroPendencia, setErroPendencia]     = useState<string | null>(null);

  const carregarPendencias = () => {
    operacoesApi.getPendenciasInformacao(op.id).then(setPendencias).catch(() => { /* bloco só fica vazio - não bloqueia a aba */ });
  };

  const [formProposta, setFormProposta] = useState({
    banco:              'CAIXA' as PropostaBancaria['banco'],
    numeroNoBanco:      '',
    numeroIsolve:       '',
    valorFinanciado:    '',
    prazoMeses:         '',
    taxaJuros:          '',
    temIq:              false,
    sistemaAmortizacao: '' as SistemaAmortizacao | '',
    codigoProposta:     '',
    codigoAvaliacao:    '',
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // SPEC S1 (E3/E4) - abrir o modal marca "visto" (zera a bandeirinha localmente,
  // sem esperar o próximo carregamento do board). Dispara 1x por operação aberta.
  useEffect(() => {
    operacoesApi.marcarVisto(op.id)
      .then(() => onUpdate({ ...op, eventosNaoVistos: 0, temAcionavelNaoVisto: false }))
      .catch(() => { /* não bloqueia a visualização - o farol só fica potencialmente desatualizado */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id]);

  // X1 (15/07/2026, E5-bis) - Tabs controlado: abaAtiva + irParaAba permitem pular
  // pra outra aba programaticamente (botão "ver aba completa" do painel de Consultas
  // no Resumo), chamando os MESMOS efeitos colaterais de uma troca de aba normal.
  const [abaAtiva, setAbaAtiva] = useState(abaInicial ?? 'resumo');
  const irParaAba = (value: string) => {
    setAbaAtiva(value);
    onTabChange(value);
  };

  // Carrega propostas/eventos ao entrar na aba
  const onTabChange = (value: string) => {
    if (value === 'propostas' && !abaPropostas) {
      setAbaPropostas(true);
      setCarregando(true);
      propostasApi.listar(op.id)
        .then(setPropostas)
        .catch(() => setErroPropostas('Erro ao carregar propostas.'))
        .finally(() => setCarregando(false));
      // SPEC_ESTACAO1 (E6) - matriz de transição, fonte única do backend.
      propostasApi.buscarMatrizTransicao(op.id)
        .then(setMatrizTransicao)
        .catch(() => { /* controle de transição só não aparece - não bloqueia o resto da aba */ });
    }
    if (value === 'timeline' && !abaEventos) {
      setAbaEventos(true);
      setCarregandoEventos(true);
      operacoesApi.getEventos(op.id)
        .then((r) => setEventos(r.itens))
        .catch(() => setErroEventos('Erro ao carregar o histórico de eventos.'))
        .finally(() => setCarregandoEventos(false));
    }
    // SPEC S2 (E2) - gatilho (a): abrir a aba Docs dispara o vigia em background.
    // Não usa carregandoEventos-like blocking - os documentos já carregados aparecem
    // na hora; o resultado do vigia só chega (e atualiza a lista) depois, em silêncio.
    // X3 (15/07/2026, E5-bis) - mesmo gatilho também na aba/painel de Consultas: o
    // gate ao vivo provou que quem salva o PDF do SICAQ volta pra fila, não passa por
    // Docs - só a aba Docs disparar era uma lacuna de ergonomia. Mesmo ciclo real
    // (vigiarPastaOperacao → triarDocumentosPendentesOperacao), mesma idempotência
    // (documento já triado retorna JA_TRIADO sem efeito colateral).
    if ((value === 'documentos' || value === 'consultas-cadastrais') && !vigiaAutomatico) {
      setVigiaAutomatico(true);
      handleVigiar();
    }
    // SPEC S3 (E4) - carrega a última crítica de CCV (se houver) ao abrir a aba Docs OU a
    // aba Crítica CCV (UI pré-demo 05/07/2026 - o painel se mudou para a aba nova, o
    // gatilho de carregamento tinha ficado só na Docs).
    if ((value === 'documentos' || value === 'critica-ccv') && !criticaCarregada) {
      setCriticaCarregada(true);
      operacoesApi.getCriticaCcv(op.id).then(setCriticaCcv).catch(() => { /* painel só não mostra nada - não bloqueia a aba */ });
    }
    // SPEC_FILA_CONSULTA_CADASTRAL (E5) - carrega a fila ao abrir a aba dedicada.
    if (value === 'consultas-cadastrais' && !filaConsultaCarregada) {
      setFilaConsultaCarregada(true);
      operacoesApi.getFilaConsultaCadastral(op.id).then(aplicarRespostaFilaConsultaCadastral).catch(() => { /* painel só não mostra nada - não bloqueia a aba */ });
    }
    // SPEC_D (E3/E6) - abrir a aba Checklist é o gatilho de resolução (frescor).
    // Participantes não precisa mais ser (re)carregado aqui - SPEC_LAYOUT_UX_V1
    // (E3) moveu o fetch para o mount do modal (ver useEffect acima), já que a
    // seção vive no Resumo (aba default) desde a11bc11.
    if (value === 'checklist') {
      carregarChecklist();
      carregarPendencias();
      // SPEC_LAYOUT_UX_V1 (E2) - seletor de banco por chips precisa da lista de
      // propostas; carrega aqui também (idempotente, mesmo guard de !abaPropostas).
      if (!abaPropostas) {
        setAbaPropostas(true);
        propostasApi.listar(op.id).then(setPropostas).catch(() => { /* seletor só não aparece - checklist CAIXA continua visível */ });
      }
      // SPEC_LAYOUT_UX_V1 (E4/E5) - só busca o dicionário se alguma pendência
      // aberta precisar dele (evita chamada desnecessária na maioria dos casos).
      if (ocupacoesPendencia.length === 0) {
        ocupacoesApi.listar().then(setOcupacoesPendencia).catch(() => { /* seletor de ocupação na resolução fica vazio */ });
      }
    }
  };

  const carregarChecklist = () => {
    setAbaChecklist(true);
    setCarregandoChecklist(true);
    setErroChecklist(null);
    operacoesApi.getChecklist(op.id)
      .then((r) => setChecklistItens(r.itens))
      .catch(() => setErroChecklist('Erro ao carregar o checklist.'))
      .finally(() => setCarregandoChecklist(false));
  };

  const salvarNovoParticipante = async () => {
    if (!novoParticipante.parteId) {
      setErroParticipante('Selecione a parte antes de adicionar.');
      return;
    }
    setErroParticipante(null);
    setSalvandoParticipante(true);
    try {
      const criado = await operacoesApi.criarParticipante(op.id, {
        parteId: novoParticipante.parteId,
        papel: novoParticipante.papel,
        percentualComposicaoRenda: novoParticipante.percentual ? Number(novoParticipante.percentual) : undefined,
      });
      setParticipantes((prev) => [...prev, criado]);
      setNovoParticipante({ parteId: '', papel: 'COOBRIGADO', percentual: '' });
    } catch (e: any) {
      // SPEC_LAYOUT_UX_V1 (E3) - "nada de falha silenciosa": mostra o motivo
      // (ex.: soma de percentuais > 100%, validado no backend) em vez de engolir.
      setErroParticipante(e?.response?.data?.erro ?? 'Erro ao adicionar participante - tente novamente.');
    } finally {
      setSalvandoParticipante(false);
    }
  };

  const removerParticipante = async (id: string) => {
    setErroParticipante(null);
    try {
      await operacoesApi.removerParticipante(op.id, id);
    } catch {
      setErroParticipante('Erro ao remover participante - tente novamente.');
      return;
    }
    setParticipantes((prev) => prev.filter((p) => p.id !== id));
  };

  // SPEC_LAYOUT_UX_V1 (E5) - resolve a pendência: grava o dado no campo real
  // (backend decide onde - Parte/Imovel/Participante), recalcula o checklist
  // (itens saem do acinzentado para aplicável/não-aplicável) e registra na
  // timeline. Sem histórico perdido - mesmo princípio de outros motores.
  const resolverPendenciaAction = async (pendencia: PendenciaInformacao) => {
    const bruto = valorPendencia[pendencia.id];
    if (bruto === undefined || bruto === '') {
      setErroPendencia('Informe um valor antes de confirmar.');
      return;
    }
    let valor: string | number | boolean = bruto;
    if (pendencia.campo === 'composicaoRenda') valor = Number(bruto);
    if (pendencia.campo === 'usoFGTS' || CAMPOS_BOOLEAN_PENDENCIA.includes(pendencia.campo)) valor = bruto === 'true';

    setResolvendoPendenciaId(pendencia.id);
    setErroPendencia(null);
    try {
      await operacoesApi.resolverPendenciaInformacao(op.id, pendencia.id, valor);
      setPendencias((prev) => prev.filter((p) => p.id !== pendencia.id));
      carregarChecklist(); // itens saem do acinzentado para aplicável/não-aplicável
    } catch (e: any) {
      setErroPendencia(e?.response?.data?.erro ?? 'Erro ao resolver a pendência - tente novamente.');
    } finally {
      setResolvendoPendenciaId(null);
    }
  };

  // SPEC_D (E6/Bloco B) - classificar o programa: PATCH na proposta dispara
  // resolverChecklist no backend; aqui só recarregamos o checklist se a aba já
  // tiver sido aberta, para a lista popular "sem reload manual".
  const classificarPrograma = async (propostaId: string, programa: Programa | null) => {
    setClassificandoProgramaId(propostaId);
    try {
      const atualizada = await propostasApi.atualizar(op.id, propostaId, { programa });
      setPropostas((prev) => prev.map((p) => (p.id === propostaId ? atualizada : p)));
      if (abaChecklist) carregarChecklist();
    } catch {
      // formulário mínimo - falha silenciosa não bloqueia o resto do modal
    } finally {
      setClassificandoProgramaId(null);
    }
  };

  // SPEC_LAYOUT_UX_V1 (E1) - gate "Seleção de Banco": cliente escolhe o banco
  // vencedor (fluxo multibanco). Pré-requisito da transição Crédito→Jurídico.
  const [selecionandoBancoId, setSelecionandoBancoId] = useState<string | null>(null);
  const [erroSelecaoBanco, setErroSelecaoBanco]        = useState<string | null>(null);

  const selecionarBanco = async (propostaId: string) => {
    setSelecionandoBancoId(propostaId);
    setErroSelecaoBanco(null);
    try {
      await propostasApi.selecionar(op.id, propostaId);
      onUpdate({ ...op, bancoSelecionadoId: propostaId });
    } catch (e: any) {
      setErroSelecaoBanco(e?.response?.data?.erro ?? 'Erro ao selecionar o banco.');
    } finally {
      setSelecionandoBancoId(null);
    }
  };

  const salvarNovaProposta = async () => {
    setErroPropostas(null);
    try {
      const criada = await propostasApi.criar(op.id, {
        banco:              formProposta.banco,
        numeroNoBanco:      formProposta.numeroNoBanco  || undefined,
        numeroIsolve:       formProposta.numeroIsolve   || undefined,
        valorFinanciado:    formProposta.valorFinanciado ? Number(formProposta.valorFinanciado.replace(/\D/g, '')) / 100 : undefined,
        prazoMeses:         formProposta.prazoMeses      ? Number(formProposta.prazoMeses) : undefined,
        taxaJuros:          formProposta.taxaJuros       ? Number(formProposta.taxaJuros) / 100 : undefined,
        temIq:              formProposta.temIq,
        sistemaAmortizacao: formProposta.sistemaAmortizacao || undefined,
        codigoProposta:     formProposta.codigoProposta  || undefined,
        codigoAvaliacao:    formProposta.codigoAvaliacao || undefined,
      });
      setPropostas((prev) => [...prev, criada]);
      setNovaProposta(false);
      setFormProposta({ banco: 'CAIXA', numeroNoBanco: '', numeroIsolve: '', valorFinanciado: '', prazoMeses: '', taxaJuros: '', temIq: false, sistemaAmortizacao: '', codigoProposta: '', codigoAvaliacao: '' });
    } catch (err: any) {
      setErroPropostas(err?.response?.data?.erro ?? 'Erro ao salvar proposta.');
    }
  };

  const removerProposta = async (propostaId: string) => {
    try {
      await propostasApi.remover(op.id, propostaId);
      setPropostas((prev) => prev.filter((p) => p.id !== propostaId));
    } catch {
      setErroPropostas('Erro ao remover proposta.');
    }
  };

  // SPEC_ESTACAO1 (E6) - transiciona o status de UMA proposta. `matriz` já
  // garante que só oferecemos destinos válidos; motivo é exigido pela UI
  // (transicoesQueExigemMotivo) e validado de novo pelo backend. Erro 409
  // (bloqueio por pendência aberta, E3) chega com a mensagem já pronta -
  // exibida verbatim, nunca trocada por um erro genérico.
  const transicionarProposta = async (propostaId: string, statusAtual: StatusPropostaBancaria) => {
    const destino = destinoTransicao[propostaId];
    if (!destino) return;
    setErroTransicao((prev) => ({ ...prev, [propostaId]: '' }));
    setTransicionandoId(propostaId);
    try {
      const atualizada = await propostasApi.transicionar(op.id, propostaId, destino, motivoTransicao[propostaId]?.trim() || undefined);
      setPropostas((prev) => prev.map((p) => (p.id === propostaId ? atualizada : p)));
      setDestinoTransicao((prev) => ({ ...prev, [propostaId]: '' }));
      setMotivoTransicao((prev) => ({ ...prev, [propostaId]: '' }));
      setTransicaoAbertaId(null);
      // Histórico pode estar visível - recarrega para refletir a transição recém-feita.
      if (historicoAbertoId === propostaId) carregarHistoricoProposta(propostaId);
    } catch (err: any) {
      setErroTransicao((prev) => ({ ...prev, [propostaId]: err?.response?.data?.erro ?? 'Erro ao transicionar status.' }));
    } finally {
      setTransicionandoId(null);
    }
  };

  const carregarHistoricoProposta = async (propostaId: string) => {
    setCarregandoHistoricoId(propostaId);
    try {
      const movimentacoes = await propostasApi.buscarMovimentacoes(op.id, propostaId);
      setMovimentacoesPorProposta((prev) => ({ ...prev, [propostaId]: movimentacoes }));
    } catch {
      // histórico só não aparece - não bloqueia o resto do card
    } finally {
      setCarregandoHistoricoId(null);
    }
  };

  const alternarHistoricoProposta = (propostaId: string) => {
    const abrindo = historicoAbertoId !== propostaId;
    setHistoricoAbertoId(abrindo ? propostaId : null);
    if (abrindo && !movimentacoesPorProposta[propostaId]) carregarHistoricoProposta(propostaId);
  };

  const abrirDrawerParte = async (parteId: string) => {
    setErroParte(null); setSucessoParte(false); setCarregandoParte(true); setParteDrawer(null);
    setRefletirPendente(false); setReflexoMensagem(null);
    try {
      const parte = await partesApi.getById(parteId);
      setParteDrawer(parte);
      setFormParte({
        ...parte,
        email: parte.email ?? '',
        telefone: parte.telefone ?? '',
        dadosBancariosCompradorTexto: parte.dadosBancariosComprador ? JSON.stringify(parte.dadosBancariosComprador, null, 2) : '',
      } as any);
    } catch {
      setErroParte('Não foi possível carregar os dados da parte.');
    } finally {
      setCarregandoParte(false);
    }
  };

  const salvarParte = async () => {
    if (!parteDrawer) return;
    setSalvandoParte(true); setErroParte(null); setSucessoParte(false);
    try {
      const payload: Record<string, unknown> = {
        nome: (formParte as any).nome || undefined,
        email: (formParte as any).email || undefined,
        telefone: (formParte as any).telefone || undefined,
        estadoCivil: (formParte as any).estadoCivil || undefined,
        estadoCivilDetalhado: (formParte as any).estadoCivilDetalhado || undefined,
        estadoCivilCurado: (formParte as any).estadoCivilCurado || undefined,
      };

      for (const { key } of CAMPOS_BOOLEANO_PARTE) {
        const v = (formParte as any)[key];
        if (v !== undefined) payload[key] = v;
      }

      for (const secao of SECOES_PARTE) {
        for (const campo of secao.campos) {
          const valor = (formParte as any)[campo.key];
          if (valor === undefined || valor === null || valor === '') continue;
          if (campo.tipo === 'number') {
            const n = Number(String(valor).replace(/\./g, '').replace(',', '.'));
            if (!Number.isNaN(n)) payload[campo.key] = n;
          } else {
            payload[campo.key] = valor;
          }
        }
      }

      const bancosRaw = (formParte as any).dadosBancariosCompradorTexto;
      if (bancosRaw) {
        try { payload.dadosBancariosComprador = JSON.parse(bancosRaw); } catch { /* JSON inválido - não envia */ }
      }

      const atualizado = await partesApi.atualizar(parteDrawer.id, payload as any);
      setParteDrawer(atualizado);
      setSucessoParte(true);
      setTimeout(() => setSucessoParte(false), 2500);

      // Write-back CRM → iSolve: confirmação in-screen (box abaixo), sem diálogo nativo.
      setReflexoMensagem(null);
      setRefletirPendente(true);
    } catch (err: any) {
      setErroParte(err?.response?.data?.erro ?? 'Erro ao salvar.');
    } finally {
      setSalvandoParte(false);
    }
  };

  const refletirAgora = async () => {
    if (!parteDrawer) return;
    setRefletindo(true); setReflexoMensagem(null);
    try {
      const r = await operacoesApi.refletirNoIsolve(op.id, { escopo: 'PARTE', parteId: parteDrawer.id });
      setRefletirPendente(false);
      if (r.ok) {
        setReflexoMensagem({ tipo: 'sucesso', texto: `Replicado no iSolve (${r.camposAplicados ?? 0} campo(s)).` });
        toast.success(`Replicado no iSolve (${r.camposAplicados ?? 0} campo(s)).`);
      } else if (r.gateDesligado) {
        setReflexoMensagem({ tipo: 'aviso', texto: r.motivo ?? 'Write-back no iSolve desabilitado no ambiente.' });
      } else {
        setReflexoMensagem({ tipo: 'aviso', texto: r.motivo ?? 'Não replicado no iSolve.' });
      }
    } catch (err: any) {
      setRefletirPendente(false);
      const erroDiscriminador = err?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) {
        toastErroISolve(erroDiscriminador, navigate);
        setReflexoMensagem({ tipo: 'erro', texto: 'Não replicado - sessão iSolve indisponível. Reconecte e tente de novo.' });
      } else {
        setReflexoMensagem({ tipo: 'erro', texto: err?.response?.data?.erro ?? 'Não replicado - erro ao chamar o iSolve.' });
      }
    } finally {
      setRefletindo(false);
    }
  };

  const carregarRecoleta = async () => {
    setRecoletaCarregando(true); setRecoletaErro(null); setRecoletaAberta(true);
    try {
      const r = await operacoesApi.recoletarIsolve(op.id);
      if (r.semIdCstimob) { setRecoletaItens([]); setRecoletaErro('Operação sem vínculo com o iSolve (idCstimob).'); return; }
      setRecoletaItens(r.itens);
      setRecoletaSelecao(new Set(r.itens.map((i) => i.chave)));
    } catch (err: any) {
      const erroDiscriminador = err?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) toastErroISolve(erroDiscriminador, navigate);
      setRecoletaErro(err?.response?.data?.erro ?? 'Falha ao recoletar do iSolve.');
    } finally {
      setRecoletaCarregando(false);
    }
  };

  const aplicarRecoletaUi = async () => {
    if (recoletaSelecao.size === 0) return;
    setRecoletaAplicando(true); setRecoletaErro(null);
    try {
      const r = await operacoesApi.aplicarRecoletaIsolve(op.id, [...recoletaSelecao]);
      if (r.ok) {
        toast.success(`Recoleta aplicada (${r.aplicados} campo(s)).`);
        setRecoletaAberta(false); setRecoletaItens([]); setRecoletaSelecao(new Set());
        const atualizada = await operacoesApi.getById(op.id);
        onUpdate(atualizada);
      } else {
        setRecoletaErro(r.motivo ?? 'Nada foi aplicado.');
      }
    } catch (err: any) {
      const erroDiscriminador = err?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) toastErroISolve(erroDiscriminador, navigate);
      else setRecoletaErro(err?.response?.data?.erro ?? 'Falha ao aplicar recoleta.');
    } finally {
      setRecoletaAplicando(false);
    }
  };

  const toggleClienteConduz = async () => {
    setSalvando(true);
    try {
      const updated = await operacoesApi.toggleClienteConduz(op.id, !clienteConduz);
      setClienteConduz(!clienteConduz);
      onUpdate(updated);
    } catch (e) { console.error(e); }
    finally { setSalvando(false); }
  };

  const config = ESTEIRAS_CONFIG[op.esteira];

  // Rótulos das abas com contador
  const tabLabel = (base: string, count?: number) =>
    count ? `${base} (${count})` : base;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {whatsModalOpen && <EnviarWhatsAppModal op={op} onClose={() => setWhatsModalOpen(false)} />}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">

        {/* ── HEADER ── */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-100 shrink-0">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="flex-1 min-w-0">
              {/* Linha de badges */}
              <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                <span className="text-xs text-slate-400 font-mono">{op.numeroProcesso}</span>
                <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[op.status] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  {op.status === 'CONGELADA' ? (<span className="inline-flex items-center gap-1"><Snowflake size={11} /> Congelada</span>) : op.status}
                </span>
                <span className="text-[11px] text-slate-400">
                  Esteira {op.esteira} · {config.nome}
                </span>
                {pendenciasOperacao.length > 0 && (
                  <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                    <AlertTriangle size={11} className="mr-0.5" /> {pendenciasOperacao.length} divergência{pendenciasOperacao.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Nome do comprador */}
              <h2 className="text-lg font-bold text-slate-900 truncate leading-tight">
                {op.compradores[0]?.nome ?? '-'}
                {op.compradores.length > 1 && (
                  <span className="text-sm font-normal text-slate-400 ml-1.5">+{op.compradores.length - 1}</span>
                )}
              </h2>

              {/* Localização · Valor */}
              <p className="text-sm text-slate-500 mt-0.5">
                {op.imovelDetalhe?.cidade
                  ? `${op.imovelDetalhe.cidade}${op.imovelDetalhe.estado ? `/${op.imovelDetalhe.estado}` : ''}`
                  : (op.enderecoImovel ?? '')}
                {(op.imovelDetalhe?.cidade || op.enderecoImovel) && op.valorImovel ? ' · ' : ''}
                {op.valorImovel ? formatMoeda(op.valorImovel) : (!op.imovelDetalhe?.cidade && !op.enderecoImovel ? '' : '')}
              </p>
            </div>

            <div className="shrink-0 flex items-center gap-2 mt-0.5">
              {isAdmin && (
                <button
                  onClick={() => { setMostrarExcluir((v) => !v); setErroExcluir(null); setConfirmacaoExcluir(''); }}
                  className="text-xs font-medium text-red-600 border border-red-200 hover:border-red-300 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Excluir operacao
                </button>
              )}
            </div>
          </div>
        </div>

        {isAdmin && mostrarExcluir && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-200 shrink-0">
            <p className="text-sm font-semibold text-red-700 mb-1">Excluir operacao {op.numeroProcesso}?</p>
            <p className="text-xs text-red-600 mb-2">
              Acao DEFINITIVA: apaga a operacao e todos os dados ligados a ela (propostas, documentos, participantes, consultas, etc.).
              Os arquivos no SharePoint nao sao removidos. Digite o numero da operacao para confirmar.
            </p>
            <div className="flex items-center gap-2">
              <input
                value={confirmacaoExcluir}
                onChange={(e) => setConfirmacaoExcluir(e.target.value)}
                placeholder={op.numeroProcesso}
                className="text-sm border border-red-300 rounded-lg px-3 py-1.5 w-56 focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <button
                onClick={handleExcluir}
                disabled={excluindo || confirmacaoExcluir.trim() !== op.numeroProcesso}
                className="text-xs font-medium text-white bg-red-600 px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {excluindo ? 'Excluindo...' : 'Confirmar exclusao'}
              </button>
              <button
                onClick={() => { setMostrarExcluir(false); setConfirmacaoExcluir(''); setErroExcluir(null); }}
                disabled={excluindo}
                className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
            {erroExcluir && <p className="text-xs text-red-600 mt-2">{erroExcluir}</p>}
          </div>
        )}

        {/* ── TABS (shadcn) ── */}
        {/* X1 (15/07/2026, E5-bis) - controlado (não mais defaultValue) para permitir
            pular pra outra aba programaticamente a partir de um botão (painel de
            Consultas no Resumo → "ver aba completa"), via irParaAba(). */}
        <Tabs value={abaAtiva} onValueChange={irParaAba} className="flex-1 flex flex-col overflow-hidden">
          <div className="border-b border-slate-100 px-2 shrink-0">
            <TabsList className="h-auto bg-transparent p-0 gap-0 w-full justify-start overflow-x-auto">
              {([
                { value: 'resumo',     label: 'Resumo' },
                { value: 'propostas',  label: tabLabel('Propostas', propostas.length || undefined) },
                { value: 'documentos', label: tabLabel('Docs', op.documentosPendentes || undefined) },
                { value: 'critica-ccv', label: 'Crítica CCV' },
                { value: 'consultas-cadastrais', label: tabLabel('Consultas', filaConsultaCadastral.filter((i) => i.status === 'COM_APONTAMENTOS').length || undefined) },
                { value: 'mensagens',  label: tabLabel('Mensagens', op.mensagensNaoLidas || undefined) },
                { value: 'comentarios', label: 'Comentários' },
                { value: 'tarefas',    label: tabLabel('Tarefas', op.tarefasPendentes || undefined) },
                { value: 'checklist',  label: tabLabel('Checklist', checklistItens.filter((i) => i.status === 'PENDENTE' || i.status === 'VENCIDO').length || undefined) },
                { value: 'timeline',   label: 'Timeline' },
              ]).map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="rounded-none px-3 py-3 text-sm font-medium bg-transparent border-b-2 border-transparent
                    text-slate-500 hover:text-slate-700
                    data-[state=active]:border-otm-orange data-[state=active]:text-blue-600
                    data-[state=active]:shadow-none data-[state=active]:bg-transparent
                    whitespace-nowrap transition-colors"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* ── RESUMO ── */}
          <TabsContent value="resumo" className="flex-1 overflow-y-auto p-5 space-y-5 mt-0">

            {/* UI pré-demo 05/07/2026 - pendências da operação (todos os tipos), reusa
                DivergenciasMini/endpoint da fila. Oculto quando não há pendências. */}
            {pendenciasOperacao.length > 0 && (
              <>
                <Section title="Pendências da operação">
                  <DivergenciasMini divergencias={pendenciasOperacao} onResolvido={removerPendencia} />
                </Section>
                <Separator />
              </>
            )}

            {/* OTMC-DEC-0006 D6.1 (17/08/2026) - disparo da Simulação CAIXA é um BOTÃO
                na ficha, não uma mudança de status: `SIMULACAO_STATUS_GATILHO` deixou
                de ser o caminho principal. O componente é autocontido (busca perfil,
                conversa com a extensão e lista os cenários) para não engrossar este
                arquivo, que já é grande. */}
            <Section title="Simulação CAIXA">
              <SimulacaoCaixaCard operacaoId={op.id} operacaoNumero={op.numeroProcesso} />
            </Section>
            <Separator />

            {/* X1 (15/07/2026, E5-bis) - painel colapsado: ponto de entrada PRINCIPAL da
                fila de Consultas Cadastrais SICAQ (a aba "Consultas" continua existindo
                como vista detalhada - link "Ver todas" abaixo leva pra lá). Mais enxuto
                que a aba: sem quebra de SERASA/SCPC nem link do PDF, só o essencial pra
                decidir se precisa agir. */}
            {(filaConsultaCadastral.length > 0 || documentosSicaqSemExtracao.length > 0) && (
              <>
                <Section title={tabLabel('Consultas Cadastrais SICAQ', filaConsultaCadastral.filter((i) => i.status === 'COM_APONTAMENTOS').length || undefined)}>
                  {erroReextrair && (
                    <p className="text-xs text-red-600 mb-2">{erroReextrair}</p>
                  )}
                  <div className="space-y-1.5">
                    {filaConsultaCadastral.map((item) => (
                      <div key={item.parteId} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg px-3 py-2">
                        <span className="text-sm text-slate-800 truncate">{item.nome}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${
                            item.status === 'COM_APONTAMENTOS' ? 'bg-red-50 text-red-700 border-red-200' :
                            item.status === 'NADA_CONSTA'       ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                                   'bg-slate-100 text-slate-500 border-slate-200'
                          }`}>
                            {item.status === 'COM_APONTAMENTOS' ? 'Com apontamentos' : item.status === 'NADA_CONSTA' ? 'Nada consta' : 'Pendente'}
                          </span>
                          {item.cpf && (
                            <button
                              type="button"
                              onClick={() => handleCopiarCpf(item.parteId, item.cpf!)}
                              className="text-slate-400 hover:text-blue-600 transition-colors"
                              title="Copiar CPF e abrir SICAQ"
                            >
                              {cpfCopiado === item.parteId ? <Check size={15} /> : <Copy size={15} />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* X4-b (15/07/2026, E5-bis) - documento SICAQ triado sem extração ainda
                        (gatilho automático falhou ou nunca rodou). Nível da operação, não
                        do personagem - Documento não tem parteId próprio no schema. */}
                    {documentosSicaqSemExtracao.map((docSemExtracao) => (
                      <div key={docSemExtracao.documentoId} className="flex items-center justify-between gap-2 border border-amber-200 bg-amber-50/50 rounded-lg px-3 py-2">
                        <span className="text-sm text-slate-700 truncate">{docSemExtracao.nomeArquivo}</span>
                        <button
                          type="button"
                          onClick={() => handleReextrairConsultaCadastral(docSemExtracao.documentoId)}
                          disabled={reextraindoId === docSemExtracao.documentoId}
                          className="text-xs font-medium text-amber-700 bg-amber-100 px-2.5 py-1 rounded-lg hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                        >
                          {reextraindoId === docSemExtracao.documentoId ? 'Reextraindo…' : <span className="inline-flex items-center gap-1"><RotateCw size={13} /> Reextrair</span>}
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => irParaAba('consultas-cadastrais')}
                      className="text-xs text-blue-600 hover:underline pt-0.5"
                    >
                      Ver todas na aba Consultas →
                    </button>
                  </div>
                </Section>
                <Separator />
              </>
            )}

            <Section title="Partes">
              <div className="space-y-1.5">
                {op.compradores.map((c) => (
                  <button key={c.id} type="button" onClick={() => abrirDrawerParte(c.id)}
                    className="w-full text-left rounded-xl hover:bg-slate-50 px-3 py-2 transition-colors group border border-transparent hover:border-slate-200">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Comprador</span>
                      <span className="text-sm text-slate-800 font-medium group-hover:text-blue-700">{c.nome}</span>
                      {c.cpf  && <span className="text-xs text-slate-400 font-mono">{formatDoc(c.cpf)}</span>}
                      {c.cnpj && <span className="text-xs text-slate-400 font-mono">{formatDoc(c.cnpj)}</span>}
                    </div>
                    {(c.email || c.telefone) && (
                      <p className="text-xs text-slate-400 mt-0.5">{[c.email, c.telefone].filter(Boolean).join(' · ')}</p>
                    )}
                  </button>
                ))}
                {op.vendedores.map((v) => (
                  <button key={v.id} type="button" onClick={() => abrirDrawerParte(v.id)}
                    className="w-full text-left rounded-xl hover:bg-slate-50 px-3 py-2 transition-colors group border border-transparent hover:border-slate-200">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold">Vendedor</span>
                      <span className="text-sm text-slate-800 font-medium group-hover:text-blue-700">{v.nome}</span>
                      {v.cpf  && <span className="text-xs text-slate-400 font-mono">{formatDoc(v.cpf)}</span>}
                      {v.cnpj && <span className="text-xs text-slate-400 font-mono">{formatDoc(v.cnpj)}</span>}
                    </div>
                    {(v.email || v.telefone) && (
                      <p className="text-xs text-slate-400 mt-0.5">{[v.email, v.telefone].filter(Boolean).join(' · ')}</p>
                    )}
                  </button>
                ))}
                {op.enNome && (
                  <div className="flex items-center gap-2 px-3 py-1.5">
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">EN (QA)</span>
                    <span className="text-sm text-slate-700">{op.enNome}</span>
                  </div>
                )}
              </div>
            </Section>

            {/* Recoleta sob demanda do iSolve (iSolve → CRM) - diff + aprovação humana.
                Só leitura no iSolve; a escrita é no CRM, por aprovação. */}
            <Section title="Recoletar do iSolve">
              <div className="space-y-2">
                {!recoletaAberta && (
                  <button type="button" onClick={carregarRecoleta}
                    className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors">
                    ⟳ Recoletar do iSolve
                  </button>
                )}
                {recoletaCarregando && <p className="text-xs text-slate-400">Buscando dados no iSolve…</p>}
                {recoletaErro && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{recoletaErro}</p>}
                {recoletaAberta && !recoletaCarregando && !recoletaErro && (
                  recoletaItens.length === 0 ? (
                    <p className="text-xs text-slate-400">Nenhuma novidade - CRM já está igual ao iSolve.</p>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500">{recoletaItens.length} campo(s) com novidade. Desmarque o que não quiser aplicar.</p>
                      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                        {recoletaItens.map((it) => (
                          <label key={it.chave} className="flex items-start gap-2 text-xs border border-slate-100 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
                            <input type="checkbox" className="mt-0.5 rounded"
                              checked={recoletaSelecao.has(it.chave)}
                              onChange={(e) => setRecoletaSelecao((s) => {
                                const n = new Set(s);
                                if (e.target.checked) n.add(it.chave); else n.delete(it.chave);
                                return n;
                              })} />
                            <span className="flex-1 min-w-0">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold mr-1 ${it.categoria === 'NOVO' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {it.categoria}
                              </span>
                              <span className="text-slate-500">{it.referencia} · </span>
                              <span className="font-mono text-slate-600">{it.campo}</span>
                              <span className="block text-slate-400 truncate">
                                {String(it.valorAtual ?? '-')} → <span className="text-slate-700 font-medium">{String(it.valorNovo ?? '-')}</span>
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button type="button" onClick={aplicarRecoletaUi} disabled={recoletaAplicando || recoletaSelecao.size === 0}
                          className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors">
                          {recoletaAplicando ? 'Aplicando…' : `Aplicar selecionadas (${recoletaSelecao.size})`}
                        </button>
                        <button type="button" onClick={() => { setRecoletaAberta(false); setRecoletaItens([]); setRecoletaErro(null); }}
                          className="text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors">
                          Recusar
                        </button>
                      </div>
                    </>
                  )
                )}
              </div>
            </Section>

            {/* Participantes (S-26/L21) - UI mínima. Movido da aba Checklist para o Resumo,
                logo abaixo de Partes (UI pré-demo 05/07/2026, PASSO 1). */}
            <Section title="Participantes">
              <div className="space-y-2">
                {participantes.length === 0 && (
                  <p className="text-xs text-slate-400">Nenhum participante além de compradores/vendedores.</p>
                )}
                {participantes.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm border border-slate-100 rounded-lg px-3 py-2">
                    <span className="flex-1 truncate">{p.parte.nome}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-semibold">{p.papel}</span>
                    <span className="text-xs text-slate-400 w-14 text-right">{p.percentualComposicaoRenda != null ? `${p.percentualComposicaoRenda}%` : '-'}</span>
                    <button type="button" onClick={() => removerParticipante(p.id)} className="text-slate-300 hover:text-red-500 text-xs">✕</button>
                  </div>
                ))}
                {participantes.length > 0 && (() => {
                  const soma = participantes.reduce((s, p) => s + Number(p.percentualComposicaoRenda ?? 0), 0);
                  return Math.abs(soma - 100) > 0.01 ? (
                    <p className="text-xs text-amber-600">⚠ Soma da composição de renda = {soma}% (esperado 100%).</p>
                  ) : null;
                })()}

                <div className="flex items-center gap-1.5 pt-1">
                  <select
                    value={novoParticipante.parteId}
                    onChange={(e) => setNovoParticipante((s) => ({ ...s, parteId: e.target.value }))}
                    className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                  >
                    <option value="">Selecione a parte…</option>
                    {[...op.compradores, ...op.vendedores].map((pessoa) => (
                      <option key={pessoa.id} value={pessoa.id}>{pessoa.nome}</option>
                    ))}
                  </select>
                  <select
                    value={novoParticipante.papel}
                    onChange={(e) => setNovoParticipante((s) => ({ ...s, papel: e.target.value as PapelParticipante }))}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                  >
                    <option value="PROPONENTE">Proponente</option>
                    <option value="COOBRIGADO">Coobrigado</option>
                    <option value="CONJUGE">Cônjuge</option>
                    <option value="ANUENTE">Anuente</option>
                  </select>
                  <input
                    type="number"
                    placeholder="%"
                    value={novoParticipante.percentual}
                    onChange={(e) => setNovoParticipante((s) => ({ ...s, percentual: e.target.value }))}
                    className="w-16 text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                  />
                  <button
                    type="button"
                    disabled={!novoParticipante.parteId || salvandoParticipante}
                    onClick={salvarNovoParticipante}
                    className="text-xs bg-otm-orange text-white px-3 py-1.5 rounded-lg disabled:opacity-40"
                  >
                    {salvandoParticipante ? 'Adicionando…' : 'Adicionar'}
                  </button>
                </div>
                {erroParticipante && <p className="text-xs text-red-500">{erroParticipante}</p>}
              </div>
            </Section>

            <Separator />

            {/* Imóvel */}
            {op.imovelDetalhe && (
              <>
                <Section title="Imóvel">
                  <div className="bg-slate-50 rounded-xl border border-slate-100 p-3 space-y-1">
                    {(op.imovelDetalhe.logradouro || op.imovelDetalhe.cidade) && (
                      <p className="text-sm text-slate-700">
                        {[op.imovelDetalhe.logradouro, op.imovelDetalhe.numero, op.imovelDetalhe.complemento].filter(Boolean).join(', ')}
                        {op.imovelDetalhe.bairro ? ` - ${op.imovelDetalhe.bairro}` : ''}
                      </p>
                    )}
                    {(op.imovelDetalhe.cidade || op.imovelDetalhe.estado) && (
                      <p className="text-sm font-medium text-slate-600">
                        {[op.imovelDetalhe.cidade, op.imovelDetalhe.estado].filter(Boolean).join('/')}
                        {op.imovelDetalhe.cep ? ` · CEP ${op.imovelDetalhe.cep}` : ''}
                      </p>
                    )}
                    {op.imovelDetalhe.matricula && (() => {
                      const validade = op.imovelDetalhe!.matriculaDataValidade ? new Date(op.imovelDetalhe!.matriculaDataValidade) : null;
                      const diasRestantes = validade ? Math.ceil((validade.getTime() - Date.now()) / 86_400_000) : null;
                      const vencida      = diasRestantes != null && diasRestantes < 0;
                      const vencendoBrve = diasRestantes != null && diasRestantes >= 0 && diasRestantes <= 7;
                      return (
                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-xs text-slate-600">Matrícula: {op.imovelDetalhe!.matricula}</span>
                          {validade && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${vencida ? 'bg-red-100 text-red-700' : vencendoBrve ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {vencida ? `Vencida ${validade.toLocaleDateString('pt-BR')}` : `Válida até ${validade.toLocaleDateString('pt-BR')}`}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </Section>
                <Separator />
              </>
            )}

            {/* Financeiro */}
            <Section title="Financeiro">
              <div className="grid grid-cols-2 gap-2">
                <DataCard
                  label="Valor do imóvel (desejado)"
                  value={op.valorImovelDesejado != null ? formatMoeda(op.valorImovelDesejado) : '-'}
                />
                <DataCard
                  label="Valor a financiar (desejado)"
                  value={op.valorFinanciamentoDesejado != null ? formatMoeda(op.valorFinanciamentoDesejado) : '-'}
                />
              </div>
            </Section>

            {/* SPEC_D (E4/E6) - motores em dois tempos: chip provisório/definitivo */}
            {(op.esteiraAltoPadraoProvisorio != null || op.vedacaoImovelProvisoria != null || op.limiteFgtsExcedeProvisorio != null) && (
              <Section title="Motores em dois tempos">
                <div className="flex flex-wrap gap-1.5">
                  <DoisTemposChip label="Alto padrão" provisorio={op.esteiraAltoPadraoProvisorio} definitivo={op.esteiraAltoPadraoDefinitivo} />
                  <DoisTemposChip label="Vedação imóvel" provisorio={op.vedacaoImovelProvisoria} definitivo={op.vedacaoImovelDefinitiva} />
                  <DoisTemposChip label="Limite FGTS excede" provisorio={op.limiteFgtsExcedeProvisorio} definitivo={op.limiteFgtsExcedeDefinitivo} />
                </div>
              </Section>
            )}

            {/* Bancos */}
            {op.bancos.length > 0 && (
              <>
                <Separator />
                <Section title="Bancos">
                  <div className="space-y-2">
                    {op.bancos.map((banco) => (
                      <div key={banco.id} className="flex items-center justify-between bg-slate-50 rounded-xl border border-slate-100 px-3 py-2.5">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{banco.nome}</p>
                          {banco.valor && <p className="text-xs text-slate-500">{formatMoeda(banco.valor)} · {banco.taxa}% a.a. · {banco.prazo}m</p>}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                          banco.status === 'APROVADO'  ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                          banco.status === 'NEGADO'    ? 'bg-red-100 text-red-600 border-red-200'            :
                          banco.status === 'CONGELADO' ? 'bg-slate-200 text-slate-600 border-slate-300'       :
                                                          'bg-amber-100 text-amber-700 border-amber-200'
                        }`}>{banco.status}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              </>
            )}

            {/* Congelamento */}
            {op.status === 'CONGELADA' && (
              <>
                <Separator />
                <section className="bg-slate-100 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1 flex items-center gap-1"><Snowflake size={11} /> Motivo do Congelamento</p>
                  <p className="text-sm text-slate-700">{op.motivoCongelamento ?? '-'}</p>
                  {op.prazoRetornoCongelamento && (
                    <p className="text-xs text-slate-500 mt-1">Prazo retorno: {op.prazoRetornoCongelamento}</p>
                  )}
                </section>
              </>
            )}

            {/* Cliente conduz (esteira Registro - bucket 3 desde SPEC_LAYOUT_UX_V1/E1) */}
            {op.esteira === 3 && (
              <>
                <Separator />
                <section className="flex items-center justify-between bg-teal-50 rounded-xl border border-teal-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-teal-800">Cliente conduz o registro</p>
                    <p className="text-xs text-teal-600">Ativar quando o cliente leva ao cartório</p>
                  </div>
                  <button
                    onClick={toggleClienteConduz}
                    disabled={salvando}
                    className={`w-11 h-6 rounded-full transition-colors relative ${clienteConduz ? 'bg-teal-500' : 'bg-slate-300'} disabled:opacity-50`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${clienteConduz ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </section>
              </>
            )}

            <Separator />

            {/* Origem */}
            <Section title="Origem">
              <div className="bg-slate-50 rounded-xl border border-slate-100 p-3 space-y-1 text-sm">
                <p className="text-slate-800 font-medium">
                  {op.origem}
                  {op.origem === '5A' ? ' - Quinto Andar' : op.origem === 'S4' ? ' - S.Quatro' : ' - Varejo'}
                </p>
                {op.offerId && <p className="text-xs text-slate-500 font-mono">Offer ID: {op.offerId}</p>}
                <p className="text-xs text-slate-500">Criado em: {new Date(op.createdAt).toLocaleDateString('pt-BR')}</p>
              </div>
            </Section>

            {/* SharePoint */}
            {op.urlPastaSharePoint && (
              <a href={op.urlPastaSharePoint} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Abrir pasta no SharePoint
              </a>
            )}
          </TabsContent>

          {/* ── PROPOSTAS ── */}
          <TabsContent value="propostas" className="flex-1 overflow-y-auto p-5 space-y-4 mt-0">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Propostas simultâneas por banco</p>
              <button
                onClick={() => setNovaProposta(!novaPropostaAberta)}
                className="text-xs px-3 py-1.5 bg-otm-orange text-white rounded-lg font-semibold hover:bg-otm-orange-dark transition-colors"
              >
                {novaPropostaAberta ? 'Cancelar' : '+ Nova proposta'}
              </button>
            </div>

            {novaPropostaAberta && (
              <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-blue-800">Nova proposta bancária</h4>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Banco *</label>
                  <div className="flex gap-2 flex-wrap">
                    {(['CAIXA', 'ITAU', 'SANTANDER', 'BRADESCO', 'INTER', 'OUTRO'] as const).map((b) => (
                      <button key={b} type="button" onClick={() => setFormProposta((f) => ({ ...f, banco: b }))}
                        className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${formProposta.banco === b ? 'bg-otm-orange text-white border-otm-orange' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'numeroNoBanco', label: 'Nº no banco', placeholder: 'Ex: 12345-6' },
                    { key: 'numeroIsolve',  label: 'Nº iSolve',   placeholder: 'Ex: IS-2026-001' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">{label}</label>
                      <input type="text" value={(formProposta as any)[key]} onChange={(e) => setFormProposta((f) => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Valor fin.</label>
                    <input type="text" inputMode="numeric" value={formProposta.valorFinanciado}
                      onChange={(e) => setFormProposta((f) => ({ ...f, valorFinanciado: e.target.value }))} placeholder="R$"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Prazo (m)</label>
                    <input type="number" value={formProposta.prazoMeses}
                      onChange={(e) => setFormProposta((f) => ({ ...f, prazoMeses: e.target.value }))} placeholder="360"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Taxa % a.a.</label>
                    <input type="number" step="0.01" value={formProposta.taxaJuros}
                      onChange={(e) => setFormProposta((f) => ({ ...f, taxaJuros: e.target.value }))} placeholder="10,99"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                {formProposta.banco === 'CAIXA' && (
                  <div className="grid grid-cols-2 gap-3 pt-1 border-t border-blue-200">
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Cód. proposta</label>
                      <input type="text" value={formProposta.codigoProposta}
                        onChange={(e) => setFormProposta((f) => ({ ...f, codigoProposta: e.target.value }))} placeholder="SICAQ/SIOPI"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Cód. avaliação</label>
                      <input type="text" value={formProposta.codigoAvaliacao}
                        onChange={(e) => setFormProposta((f) => ({ ...f, codigoAvaliacao: e.target.value }))}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Amortização</label>
                      <select value={formProposta.sistemaAmortizacao}
                        onChange={(e) => setFormProposta((f) => ({ ...f, sistemaAmortizacao: e.target.value as any }))}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                        <option value="">-</option>
                        <option value="SAC">SAC</option>
                        <option value="PRICE">PRICE</option>
                      </select>
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={formProposta.temIq}
                    onChange={(e) => setFormProposta((f) => ({ ...f, temIq: e.target.checked }))} className="rounded" />
                  Tem IQ (Instrução de Qualificação)
                </label>
                {erroPropostas && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded">{erroPropostas}</p>
                )}
                <button onClick={salvarNovaProposta}
                  className="w-full py-2 bg-otm-orange text-white text-sm font-semibold rounded-lg hover:bg-otm-orange-dark transition-colors">
                  Salvar proposta
                </button>
              </div>
            )}

            {carregandoPropostas ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-otm-orange border-t-transparent rounded-full animate-spin" />
              </div>
            ) : propostas.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Nenhuma proposta cadastrada</p>
            ) : (
              <div className="space-y-3">
                {propostas.map((p) => {
                  const selecionada = op.bancoSelecionadoId === p.id;
                  // SPEC_BLOCO3 (14/07/2026) - condicaoVigente (D6) só existe com
                  // resposta registrada; sem resposta, mostra a rodada aberta (solicitado).
                  const rodadaExibida = p.condicaoVigente ?? p.ultimaRodada;
                  const aguardandoResposta = !p.condicaoVigente && !!p.ultimaRodada;
                  return (
                  <div key={p.id} className={`border rounded-xl p-3.5 bg-white shadow-sm ${selecionada ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-800">{p.banco}</span>
                        {p.numeroNoBanco && <span className="text-xs text-slate-500 font-mono">#{p.numeroNoBanco}</span>}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${COR_STATUS_PROPOSTA[p.status] ?? 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                          {LABEL_STATUS_PROPOSTA[p.status] ?? p.status}
                        </span>
                        {p.temIq && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">IQ</span>}
                        {selecionada && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">✓ Banco selecionado</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!selecionada && (
                          <button
                            onClick={() => selecionarBanco(p.id)}
                            disabled={selecionandoBancoId === p.id}
                            className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                            title="Escolher este banco como vencedor (gate Crédito→Jurídico)"
                          >
                            {selecionandoBancoId === p.id ? 'Selecionando…' : 'Selecionar como vencedor'}
                          </button>
                        )}
                        <button onClick={() => removerProposta(p.id)}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors p-1" title="Remover proposta">
                          ✕
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div><span className="text-slate-400 block mb-0.5">Valor fin.</span><span className="font-semibold text-slate-700">{formatMoeda(rodadaExibida?.valorAprovado ?? rodadaExibida?.valorSolicitado)}</span></div>
                      <div><span className="text-slate-400 block mb-0.5">Prazo</span><span className="font-semibold text-slate-700">{(rodadaExibida?.prazoAprovadoMeses ?? rodadaExibida?.prazoSolicitadoMeses) ? `${rodadaExibida?.prazoAprovadoMeses ?? rodadaExibida?.prazoSolicitadoMeses}m` : '-'}</span></div>
                      <div><span className="text-slate-400 block mb-0.5">Taxa</span><span className="font-semibold text-slate-700">{formatPct(rodadaExibida?.taxaNominalAa)}</span></div>
                    </div>
                    {aguardandoResposta && (
                      <p className="text-[10px] text-amber-600 mt-1">Aguardando resposta do banco (rodada {p.ultimaRodada?.numeroRodada}).</p>
                    )}

                    {/* SPEC_D (E6/Bloco B) - classificar o programa dispara resolverChecklist no
                        backend; a aba Checklist é lazy e resolve de novo ao ser aberta. */}
                    <div className="mt-2.5 pt-2 border-t border-slate-100">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Programa (Checklist D)</label>
                      <select
                        value={p.programa ?? ''}
                        onChange={(e) => classificarPrograma(p.id, e.target.value ? (e.target.value as Programa) : null)}
                        disabled={classificandoProgramaId === p.id || p.banco !== 'CAIXA'}
                        className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        <option value="">Não classificado</option>
                        <option value="CCFGTS_PMCMV">CCFGTS_PMCMV</option>
                        <option value="CCSBPE">CCSBPE</option>
                        <option value="RECURSOS_LIVRES">RECURSOS_LIVRES</option>
                        <option value="PRO_COTISTA">PRO_COTISTA</option>
                      </select>
                      {p.banco !== 'CAIXA' && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          O Checklist D só resolve pela proposta CAIXA (operação multibanco - cada banco tem sua própria proposta).
                        </p>
                      )}
                    </div>
                    {(p.codigoProposta || p.codigoAvaliacao || rodadaExibida?.sistemaAmortizacao) && (
                      <div className="mt-2.5 pt-2 border-t border-slate-100 grid grid-cols-3 gap-2 text-xs">
                        {p.codigoProposta && <div><span className="text-slate-400 block">Cód. proposta</span><span className="font-mono text-slate-700">{p.codigoProposta}</span></div>}
                        {p.codigoAvaliacao && <div><span className="text-slate-400 block">Cód. avaliação</span><span className="font-mono text-slate-700">{p.codigoAvaliacao}</span></div>}
                        {rodadaExibida?.sistemaAmortizacao && <div><span className="text-slate-400 block">Amortização</span><span className="font-semibold text-slate-700">{rodadaExibida.sistemaAmortizacao}</span></div>}
                      </div>
                    )}
                    {p.numeroIsolve && <p className="text-[11px] text-slate-400 mt-1.5">iSolve: {p.numeroIsolve}</p>}

                    {/* SPEC_ESTACAO1 (E6) - controle de transição: só oferece os
                        destinos válidos do estado atual (matriz vem do backend,
                        nunca duplicada aqui). */}
                    {(matrizTransicao?.matriz[p.status]?.length ?? 0) > 0 && (
                      <div className="mt-2.5 pt-2 border-t border-slate-100">
                        {transicaoAbertaId === p.id ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <select
                                value={destinoTransicao[p.id] ?? ''}
                                onChange={(e) => setDestinoTransicao((prev) => ({ ...prev, [p.id]: e.target.value as StatusPropostaBancaria }))}
                                className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                              >
                                <option value="">Selecione o novo status…</option>
                                {matrizTransicao!.matriz[p.status].map((destino) => (
                                  <option key={destino} value={destino}>{LABEL_STATUS_PROPOSTA[destino] ?? destino}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => {
                                  setTransicaoAbertaId(null);
                                  setDestinoTransicao((prev) => ({ ...prev, [p.id]: '' }));
                                  setMotivoTransicao((prev) => ({ ...prev, [p.id]: '' }));
                                  setErroTransicao((prev) => ({ ...prev, [p.id]: '' }));
                                }}
                                className="text-xs text-slate-400 hover:text-slate-600 px-1.5"
                              >
                                Cancelar
                              </button>
                            </div>
                            {!!destinoTransicao[p.id] && matrizTransicao!.transicoesQueExigemMotivo.includes(`${p.status}->${destinoTransicao[p.id]}`) && (
                              <textarea
                                value={motivoTransicao[p.id] ?? ''}
                                onChange={(e) => setMotivoTransicao((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                placeholder="Motivo da transição de retorno (obrigatório)"
                                rows={2}
                                className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                            {erroTransicao[p.id] && (
                              <p className="text-[11px] text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded">{erroTransicao[p.id]}</p>
                            )}
                            <button
                              type="button"
                              onClick={() => transicionarProposta(p.id, p.status)}
                              disabled={
                                !destinoTransicao[p.id]
                                || transicionandoId === p.id
                                || (matrizTransicao!.transicoesQueExigemMotivo.includes(`${p.status}->${destinoTransicao[p.id]}`) && !motivoTransicao[p.id]?.trim())
                              }
                              className="w-full py-1.5 bg-otm-orange text-white text-xs font-semibold rounded-lg hover:bg-otm-orange-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {transicionandoId === p.id ? 'Transicionando…' : 'Confirmar transição'}
                            </button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setTransicaoAbertaId(p.id)} className="text-[11px] font-medium text-blue-600 hover:underline">
                            Mudar status
                          </button>
                        )}
                      </div>
                    )}

                    {/* SPEC_ESTACAO1 (E6) - histórico de transições (E2), reusa o
                        padrão visual da Timeline de eventos (linha vertical + dots). */}
                    <div className="mt-1.5">
                      <button type="button" onClick={() => alternarHistoricoProposta(p.id)} className="text-[11px] text-slate-400 hover:text-slate-600 hover:underline">
                        {historicoAbertoId === p.id ? 'ocultar histórico' : 'ver histórico'}
                      </button>
                      {historicoAbertoId === p.id && (
                        <div className="mt-2">
                          {carregandoHistoricoId === p.id ? (
                            <div className="flex items-center justify-center py-3">
                              <div className="w-4 h-4 border-2 border-otm-orange border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : !movimentacoesPorProposta[p.id]?.length ? (
                            <p className="text-[11px] text-slate-400 py-1">Sem movimentações registradas.</p>
                          ) : (
                            <div className="relative">
                              <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" />
                              <div className="space-y-1.5 pl-5">
                                {movimentacoesPorProposta[p.id].map((m) => (
                                  <div key={m.id} className="relative">
                                    <div className="absolute -left-[0.95rem] top-1 w-2 h-2 rounded-full bg-blue-400 border-2 border-white shadow-sm" />
                                    <p className="text-[11px] text-slate-600 font-semibold">
                                      {m.estagioDE === 'CRIACAO'
                                        ? 'Criada'
                                        : `${LABEL_STATUS_PROPOSTA[m.estagioDE] ?? m.estagioDE} → ${LABEL_STATUS_PROPOSTA[m.estagioParaA] ?? m.estagioParaA}`}
                                    </p>
                                    <p className="text-[10px] text-slate-400">
                                      {m.usuario} em {new Date(m.movidoEm).toLocaleString('pt-BR')}
                                      {m.motivo ? ` - ${m.motivo}` : ''}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
                {erroSelecaoBanco && <p className="text-xs text-red-500">{erroSelecaoBanco}</p>}
              </div>
            )}
          </TabsContent>

          {/* ── DOCUMENTOS ── */}
          <TabsContent value="documentos" className="flex-1 overflow-y-auto p-5 mt-0">
            <div className="flex items-center justify-between mb-3 gap-2">
              <button
                type="button"
                onClick={handleTrazerDocumentos}
                disabled={trazendoDocs}
                className="text-xs font-medium text-white bg-otm-orange px-3 py-1.5 rounded-lg hover:bg-otm-orange-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {trazendoDocs ? 'Trazendo documentos…' : 'Trazer documentos do iSolve'}
              </button>
              {/* Ações da pasta/documentos agrupadas num menu (SPEC S2/S3) */}
              <DropdownAcoes
                itens={[
                  {
                    label: vigiando ? 'Vigiando…' : 'Vigiar pasta',
                    icon: Search,
                    onClick: handleVigiar,
                    disabled: vigiando,
                    title: 'Reconcilia a pasta do SharePoint com o índice de documentos (detecta arquivos novos, sumidos ou renomeados)',
                  },
                  {
                    label: criticando ? 'Criticando…' : 'Criticar CCV',
                    icon: ScanSearch,
                    onClick: handleCriticarCcv,
                    disabled: criticando || !op.ccvArquivoId,
                    title: op.ccvArquivoId ? 'Extrai o CCV e confere campo a campo contra o cadastro' : 'Nenhum CCV ingerido nesta operação ainda',
                  },
                ]}
              />
            </div>
            {erroDocs && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-3">
                {erroDocs}
              </div>
            )}
            {erroReprocessar && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-3">
                {erroReprocessar}
              </div>
            )}
            {/* Legenda - a aba Docs é a tela onde o GATE E6 é revisado, os rótulos precisam
                ser autoexplicativos sem abrir cada documento. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[11px] text-slate-500">
              {LEGENDA_STATUS_DOC.map((l) => (
                <span key={l.status} className="flex items-center gap-1">
                  <span className={`inline-block w-2 h-2 rounded-full border ${COR_STATUS_DOC[l.status]}`} />
                  {l.rotulo}
                </span>
              ))}
            </div>
            <div className="space-y-2">
              {!op.documentos?.length ? (
                <p className="text-sm text-slate-400 text-center py-8">Nenhum documento registrado</p>
              ) : op.documentos.map((doc: Documento) => (
                <div key={doc.id} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 hover:border-slate-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.nome}</p>
                      {doc.status === 'TRIAGEM_MANUAL' && doc.observacoes && (
                        <p className="text-xs text-slate-400 truncate mt-0.5">{doc.observacoes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${COR_STATUS_DOC[doc.status]}`}>
                        {rotuloStatusDocumento(doc)}
                      </span>
                      {doc.urlSharePoint && (
                        <a href={doc.urlSharePoint} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                  {/* GATE E6 - objeto da revisão: tipo+confiança+nome proposto (antes só existia
                      em log de console, ver INT-043). */}
                  {doc.status === 'PROPOSTA_PENDENTE_APROVACAO' && (
                    <div className="mt-2 pt-2 border-t border-amber-100 bg-amber-50/50 -mx-3 -mb-2.5 px-3 py-2 rounded-b-xl text-xs space-y-0.5">
                      <p className="text-slate-600">
                        Tipo: <span className="font-semibold text-slate-800">{doc.tipoPropostoNome}</span>
                        {typeof doc.confiancaClassificacao === 'number' && (
                          <span className="text-slate-400"> · confiança {(doc.confiancaClassificacao * 100).toFixed(0)}%</span>
                        )}
                      </p>
                      <p className="text-slate-500 truncate">Nome proposto: <span className="font-mono">{doc.nomeArquivoProposto}</span></p>
                    </div>
                  )}
                  {/* X4-a (15/07/2026, E5-bis) - só aparece com falha de TRANSPORTE
                      confirmada (não ambiguidade de classificação, que precisa de
                      decisão humana - ver ehFalhaTransporteTriagem no backend). */}
                  {doc.status === 'TRIAGEM_MANUAL' && doc.falhaTransporte && (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleReprocessarTriagem(doc.id)}
                        disabled={reprocessandoId === doc.id}
                        className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {reprocessandoId === doc.id ? 'Reprocessando…' : <span className="inline-flex items-center gap-1"><RotateCw size={13} /> Reprocessar triagem</span>}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ── CRÍTICA CCV - aba própria (UI pré-demo 05/07/2026, PASSO 2). Antes vivia
              dentro da aba Docs; o painel e o botão são os mesmos, só a localização mudou. */}
          <TabsContent value="critica-ccv" className="flex-1 overflow-y-auto p-5 mt-0">
            <div className="flex items-center justify-between mb-3 gap-2">
              {/* Mesmo handler do botão da aba Docs - não duplica lógica, só a marcação. */}
              <button
                type="button"
                onClick={handleCriticarCcv}
                disabled={criticando || !op.ccvArquivoId}
                title={op.ccvArquivoId ? 'Extrai o CCV e confere campo a campo contra o cadastro' : 'Nenhum CCV ingerido nesta operação ainda'}
                className="text-xs font-medium text-slate-600 bg-slate-100 px-3 py-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
              >
                <ScanSearch size={14} />
                {criticando ? 'Criticando…' : 'Criticar CCV'}
              </button>
            </div>
            {erroCritica && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-3">
                {erroCritica}
              </div>
            )}
            {/* SPEC S3 (E4) - painel "Crítica do CCV": statusGeral + data + campo a campo */}
            {criticaCcv ? (
              <div className="border border-slate-200 rounded-xl p-3.5 bg-white shadow-sm mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-slate-800">Crítica do CCV</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${
                    criticaCcv.statusGeral === 'LIMPA' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    criticaCcv.statusGeral === 'COM_DIVERGENCIAS' ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-slate-100 text-slate-500 border-slate-200'
                  }`}>
                    {criticaCcv.statusGeral === 'LIMPA' ? 'Limpa' : criticaCcv.statusGeral === 'COM_DIVERGENCIAS' ? 'Com divergências' : 'Falha na leitura'}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 mb-2">Última crítica: {formatData(criticaCcv.criadoEm)}</p>
                {criticaCcv.resultado.length > 0 && (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {criticaCcv.resultado.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs gap-2">
                        <span className={`font-bold ${CATEGORIA_CRITICA_LABEL[item.categoria]?.cor ?? 'text-slate-400'}`}>
                          {CATEGORIA_CRITICA_LABEL[item.categoria]?.icone ?? '?'}
                        </span>
                        <span className="text-slate-600 flex-1 truncate">
                          {item.referencia ? `${item.referencia} - ${item.campo}` : item.campo}
                        </span>
                        <span className="text-slate-400 truncate max-w-[40%]" title={String(item.valorCcv ?? '')}>
                          {String(item.valorCcv ?? '-')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">Nenhuma crítica executada ainda para esta operação.</p>
            )}

            <Separator />

            {/* Divergências desta crítica - mesma DivergenciaConector (tipo=CRITICA_CCV) da
                fila geral, filtrada a partir do estado já carregado (pendenciasOperacao) para
                esta operação; mesmos handlers de aprovar/rejeitar, sem fluxo paralelo. */}
            <Section title="Divergências desta crítica">
              <DivergenciasMini
                divergencias={pendenciasOperacao.filter((d) => d.tipo === 'CRITICA_CCV')}
                onResolvido={removerPendencia}
                vazio="Nenhuma divergência pendente desta crítica."
              />
            </Section>
          </TabsContent>

          {/* ── CONSULTAS CADASTRAIS (SPEC_FILA_CONSULTA_CADASTRAL, E5) ── */}
          <TabsContent value="consultas-cadastrais" className="flex-1 overflow-y-auto p-5 mt-0">
            {filaConsultaCadastral.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Nenhum personagem pessoa física vinculado a esta operação.</p>
            ) : (
              <div className="space-y-2">
                {filaConsultaCadastral.map((item) => (
                  <div key={item.parteId} className="border border-slate-200 rounded-xl p-3.5 bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-bold text-slate-800 truncate">{item.nome}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border shrink-0 ${
                        item.status === 'COM_APONTAMENTOS' ? 'bg-red-50 text-red-700 border-red-200' :
                        item.status === 'NADA_CONSTA'       ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                               'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>
                        {item.status === 'COM_APONTAMENTOS'
                          ? `Com apontamentos${item.dataUltimaConsulta ? ` em ${formatData(item.dataUltimaConsulta)}` : ''}`
                          : item.status === 'NADA_CONSTA'
                          ? `Nada consta${item.dataUltimaConsulta ? ` em ${formatData(item.dataUltimaConsulta)}` : ''}`
                          : 'Pendente'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <span>{item.cpf ?? 'CPF não cadastrado'}</span>
                        {item.cpf && (
                          <button
                            type="button"
                            onClick={() => handleCopiarCpf(item.parteId, item.cpf!)}
                            className="text-slate-400 hover:text-blue-600 transition-colors"
                            title="Copiar CPF e abrir SICAQ"
                          >
                            {cpfCopiado === item.parteId ? <span className="inline-flex items-center gap-1"><Check size={15} /> Copiado</span> : <Copy size={15} />}
                          </button>
                        )}
                      </div>
                      {item.urlSharePoint && (
                        <a href={item.urlSharePoint} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline shrink-0">
                          Ver PDF da última consulta
                        </a>
                      )}
                    </div>
                    {item.status === 'COM_APONTAMENTOS' && (item.serasaQtde || item.scpcQtde) && (
                      <p className="text-[11px] text-slate-400 mt-1.5">
                        {item.serasaQtde ? `SERASA: ${item.serasaQtde} ocorrência(s)${item.serasaValorTotal ? ` - ${formatMoeda(item.serasaValorTotal)}` : ''}` : ''}
                        {item.serasaQtde && item.scpcQtde ? ' · ' : ''}
                        {item.scpcQtde ? `SCPC: ${item.scpcQtde} registro(s)${item.scpcValorTotal ? ` - ${formatMoeda(item.scpcValorTotal)}` : ''}` : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* X4-b (15/07/2026, E5-bis) - documentos SICAQ triados sem ConsultaCadastral
                ainda (gatilho automático falhou ou nunca rodou). Nível da operação, não
                do personagem. */}
            {erroReextrair && (
              <p className="text-xs text-red-600 mt-3">{erroReextrair}</p>
            )}
            {documentosSicaqSemExtracao.length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Documentos SICAQ aguardando extração</p>
                {documentosSicaqSemExtracao.map((docSemExtracao) => (
                  <div key={docSemExtracao.documentoId} className="flex items-center justify-between gap-2 border border-amber-200 bg-amber-50/50 rounded-xl p-3">
                    <span className="text-sm text-slate-700 truncate">{docSemExtracao.nomeArquivo}</span>
                    <button
                      type="button"
                      onClick={() => handleReextrairConsultaCadastral(docSemExtracao.documentoId)}
                      disabled={reextraindoId === docSemExtracao.documentoId}
                      className="text-xs font-medium text-amber-700 bg-amber-100 px-2.5 py-1 rounded-lg hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                    >
                      {reextraindoId === docSemExtracao.documentoId ? 'Reextraindo…' : '↻ Reextrair'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── MENSAGENS ── */}
          <TabsContent value="mensagens" className="flex-1 overflow-y-auto p-5 mt-0">
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 space-y-2">
              <p className="text-xs font-semibold text-emerald-800">Análise de crédito (WhatsApp)</p>
              <p className="text-[11px] text-emerald-700">
                Abre o WhatsApp do proponente principal {(op.compradores.find((c) => c.posicaoIsolve === 1) ?? op.compradores[0])?.nome ?? '-'} com a mensagem padrão e o link do formulário.
              </p>
              <button type="button" onClick={() => setWhatsModalOpen(true)}
                className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition-colors">
                Enviar análise de crédito
              </button>
            </div>
            <div className="space-y-2">
              {!op.mensagens?.length ? (
                <p className="text-sm text-slate-400 text-center py-8">Nenhuma mensagem registrada</p>
              ) : op.mensagens.map((msg: Mensagem) => (
                <div key={msg.id} className={`rounded-xl p-3.5 border ${msg.lida ? 'border-slate-200 bg-white' : 'border-blue-200 bg-blue-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${
                        msg.canal === 'WHATSAPP' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                        msg.canal === 'EMAIL'    ? 'bg-blue-100 text-blue-700 border-blue-200'         :
                                                   'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>{msg.canal}</span>
                      <span className="text-xs font-semibold text-slate-700">{msg.remetente}</span>
                    </div>
                    <span className="text-[10px] text-slate-400">{formatData(msg.createdAt)}</span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.conteudo}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ── TAREFAS ── */}
          <TabsContent value="tarefas" className="flex-1 overflow-y-auto p-5 mt-0">
            <div className="space-y-2">
              {!op.tarefas?.length ? (
                <p className="text-sm text-slate-400 text-center py-8">Nenhuma tarefa registrada</p>
              ) : op.tarefas.map((tarefa: Tarefa) => (
                <div key={tarefa.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-white border border-slate-200 hover:border-slate-300 transition-colors">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${tarefa.concluida ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                    {tarefa.concluida && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${tarefa.concluida ? 'line-through text-slate-400' : 'text-slate-800 font-medium'}`}>{tarefa.titulo}</p>
                    {tarefa.responsavel && <p className="text-xs text-slate-400">{tarefa.responsavel}</p>}
                  </div>
                  {tarefa.prazo && <span className="text-xs text-slate-400 shrink-0">{tarefa.prazo}</span>}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ── CHECKLIST - SPEC_D Checklist Documental (E6) ── */}
          <TabsContent value="checklist" className="flex-1 overflow-y-auto p-5 space-y-5 mt-0">
            {/* SPEC_LAYOUT_UX_V1 (E5) - bloco acinzentado-agrupado por dado
                faltante. "Fail-open zero": item cuja condição não pode ser
                resolvida por falta de dado não fica misturado nem oculto. */}
            {pendencias.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  Aguardando informação - {pendencias.length} pendência{pendencias.length !== 1 ? 's' : ''}
                </p>
                {pendencias.map((p) => {
                  const valor = valorPendencia[p.id] ?? '';
                  const setValor = (v: string) => setValorPendencia((s) => ({ ...s, [p.id]: v }));
                  return (
                    <div key={p.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                      <span className="flex-1 text-sm text-slate-600 truncate">
                        {p.campoLabel}
                        {p.participanteNome && <span className="text-slate-400"> - {p.participanteNome}</span>}
                      </span>

                      {p.campo === 'estadoCivil' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          {ESTADOS_CIVIS_PENDENCIA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      )}
                      {p.campo === 'ocupacao' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5" disabled={ocupacoesPendencia.length === 0}>
                          <option value="">{ocupacoesPendencia.length === 0 ? 'Dicionário vazio' : 'Selecione…'}</option>
                          {ocupacoesPendencia.map((o) => <option key={o.id} value={o.id}>{o.descricao}</option>)}
                        </select>
                      )}
                      {p.campo === 'tipoContratacao' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          {TIPOS_CONTRATACAO_PENDENCIA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      )}
                      {p.campo === 'condicaoImovel' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          <option value="NOVO">Novo</option>
                          <option value="USADO">Usado</option>
                        </select>
                      )}
                      {p.campo === 'composicaoRenda' && (
                        <input type="number" min={0} max={100} value={valor} onChange={(e) => setValor(e.target.value)} placeholder="%" className="w-16 text-xs border border-slate-200 rounded-lg px-2 py-1.5" />
                      )}
                      {p.campo === 'usoFGTS' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          <option value="true">Sim</option>
                          <option value="false">Não</option>
                        </select>
                      )}
                      {CAMPOS_BOOLEAN_PENDENCIA.includes(p.campo) && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          <option value="true">Sim</option>
                          <option value="false">Não</option>
                        </select>
                      )}
                      {p.campo === 'modalidadeAquisicao' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          <option value="FINANCIAMENTO">Financiamento</option>
                          <option value="A_VISTA_AUTOFINANCIAMENTO">À vista / autofinanciamento</option>
                        </select>
                      )}
                      {p.campo === 'regimeBensDesejado' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          {REGIMES_BENS_PENDENCIA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      )}
                      {p.campo === 'perdaResidencia' && (
                        <select value={valor} onChange={(e) => setValor(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                          <option value="">Selecione…</option>
                          <option value="NENHUMA">Nenhuma das duas</option>
                          <option value="SEPARACAO">Separação</option>
                          <option value="SINISTRO">Sinistro</option>
                        </select>
                      )}

                      <button
                        type="button"
                        onClick={() => resolverPendenciaAction(p)}
                        disabled={resolvendoPendenciaId === p.id}
                        className="text-xs font-medium text-white bg-slate-700 px-2.5 py-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors shrink-0"
                      >
                        {resolvendoPendenciaId === p.id ? 'Salvando…' : 'Informar'}
                      </button>
                    </div>
                  );
                })}
                {erroPendencia && <p className="text-xs text-red-500">{erroPendencia}</p>}
              </div>
            )}

            {carregandoChecklist ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-otm-orange border-t-transparent rounded-full animate-spin" />
              </div>
            ) : erroChecklist ? (
              <p className="text-sm text-red-600 text-center py-8">{erroChecklist}</p>
            ) : checklistItens.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">
                Nenhum item - verifique se há proposta com programa definido, ou se o Checklist D
                está habilitado neste ambiente.
              </p>
            ) : (() => {
              // SPEC_LAYOUT_UX_V1 (E2) - INT-004 na UI: checklist CAIXA nunca aparece
              // fora do contexto da proposta CAIXA. Mono-CAIXA (maioria da carteira) =
              // sem seletor, comportamento idêntico ao anterior (zero regressão).
              const multibanco = propostas.length > 1;
              const bancoAtivo = bancoChecklistSelecionado ?? (propostas.find((p) => p.banco === 'CAIXA')?.banco ?? propostas[0]?.banco ?? 'CAIXA');
              const itensExibidos = !multibanco || bancoAtivo === 'CAIXA'
                ? checklistItens
                : checklistItens.filter((i) => i.regra.nucleoComum);
              // SPEC_CHECKLIST_INTELIGENTE_V1_1 (E3) - balde B fica fora do
              // agrupamento por etapa, em seção própria (ChecagensExternas).
              const itensChecagemExterna = itensExibidos.filter((i) => i.regra.categoria === 'CHECAGEM_EXTERNA');
              const itensPadrao = itensExibidos.filter((i) => i.regra.categoria !== 'CHECAGEM_EXTERNA');

              return (
                <>
                  {multibanco && (
                    <div className="flex items-center gap-1.5 flex-wrap pb-1">
                      {propostas.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setBancoChecklistSelecionado(p.banco)}
                          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                            bancoAtivo === p.banco
                              ? 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {p.banco}
                        </button>
                      ))}
                    </div>
                  )}

                  {multibanco && bancoAtivo !== 'CAIXA' && itensExibidos.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">
                      Sem motor normativo para este banco - nenhuma exigência de núcleo comum cadastrada ainda.
                    </p>
                  ) : (
                    <>
                      <ChecagensExternas itens={itensChecagemExterna} operacaoId={op.id} onRegistrado={carregarChecklist} />
                      <ChecklistPorEtapa
                        itens={itensPadrao}
                        mostrarNaoAplicavel={mostrarNaoAplicavel}
                        onToggleNaoAplicavel={() => setMostrarNaoAplicavel((v) => !v)}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={carregarChecklist}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    <RotateCw size={13} /> Reprocessar checklist
                  </button>
                </>
              );
            })()}
          </TabsContent>

          {/* ── COMENTÁRIOS - Central de Comunicação (Fase A) ── */}
          <TabsContent value="comentarios" className="flex-1 min-h-0 mt-0 data-[state=active]:flex data-[state=active]:flex-col">
            <OperacaoComentarios operacaoId={op.id} />
          </TabsContent>

          {/* ── TIMELINE - feed de eventos (SPEC S1) ── */}
          <TabsContent value="timeline" className="flex-1 overflow-y-auto p-5 mt-0">
            {carregandoEventos ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-otm-orange border-t-transparent rounded-full animate-spin" />
              </div>
            ) : erroEventos ? (
              <p className="text-sm text-red-500 text-center py-8">{erroEventos}</p>
            ) : !eventos.length ? (
              <p className="text-sm text-slate-400 py-4">
                Criada em {new Date(op.createdAt).toLocaleDateString('pt-BR')}. Sem eventos registrados ainda.
              </p>
            ) : (
              <div className="relative">
                <div className="absolute left-3.5 top-0 bottom-0 w-px bg-slate-200" />
                <div className="space-y-2.5 pl-10">
                  {eventos.map((e) => {
                    const meta = EVENTO_LABEL[e.tipo] ?? { rotulo: e.tipo, icone: Circle };
                    const IconeEvento = meta.icone;
                    const cor = EVENTO_COR[e.severidade] ?? EVENTO_COR.INFO;
                    return (
                      <div key={e.id} className="relative">
                        <div
                          className={`absolute -left-[1.65rem] w-3 h-3 rounded-full border-2 border-white shadow-sm ${
                            e.severidade === 'ACIONAVEL' ? 'bg-amber-500' : 'bg-blue-500'
                          }`}
                        />
                        <div className={`border rounded-xl px-3 py-2.5 ${cor}`}>
                          <p className="text-sm font-semibold flex items-center gap-1.5">
                            <IconeEvento size={14} className="shrink-0" />
                            {meta.rotulo}
                          </p>
                          <p className="text-sm mt-0.5">{e.resumo}</p>
                          <p className="text-xs opacity-70 mt-1" title={formatData(e.criadoEm)}>
                            {formatDataRelativa(e.criadoEm)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── DRAWER DE PARTE ── */}
      <Sheet open={!!parteDrawer || carregandoParte}
        onOpenChange={(open) => { if (!open) { setParteDrawer(null); setErroParte(null); setSucessoParte(false); } }}>
        <SheetContent side="right" className="w-full sm:w-[48%] sm:max-w-none p-0 gap-0 flex flex-col bg-white">
          {carregandoParte ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-otm-orange border-t-transparent rounded-full animate-spin" />
            </div>
          ) : parteDrawer ? (
            <>
              {/* ── Header (ícone + título + identificação) ── */}
              <div className="shrink-0 px-6 pt-6 pb-4 border-b border-slate-100 pr-12">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-otm-navy/10 text-otm-navy">
                    {parteDrawer.tipo === 'PF' ? <User size={18} /> : <Building2 size={18} />}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-900 truncate leading-tight">
                      {((formParte as any).nome as string)?.trim() || parteDrawer.nome || 'Dados da parte'}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${parteDrawer.tipo === 'PF' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-purple-100 text-purple-700 border-purple-200'}`}>
                        {parteDrawer.tipo === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                      </span>
                      {parteDrawer.cpf  && <span className="text-xs font-mono text-slate-500">{formatDoc(parteDrawer.cpf)}</span>}
                      {parteDrawer.cnpj && <span className="text-xs font-mono text-slate-500">{formatDoc(parteDrawer.cnpj)}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Corpo rolável ── */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                {/* Contato */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                  <CampoForm label="Nome completo" obrigatorio className="sm:col-span-2">
                    <input type="text" value={(formParte as any).nome ?? ''}
                      onChange={(e) => setFormParte((f) => ({ ...f, nome: e.target.value }))}
                      className={INPUT_CLS} />
                  </CampoForm>
                  <CampoForm label="E-mail" ajuda="Opcional.">
                    <input type="email" value={(formParte as any).email ?? ''} placeholder="email@exemplo.com"
                      onChange={(e) => setFormParte((f) => ({ ...f, email: e.target.value }))}
                      className={INPUT_CLS} />
                  </CampoForm>
                  <CampoForm label="Telefone" ajuda="Com DDD - habilita o envio de WhatsApp.">
                    <input type="text" value={(formParte as any).telefone ?? ''} placeholder="(11) 99999-9999"
                      onChange={(e) => setFormParte((f) => ({ ...f, telefone: e.target.value }))}
                      className={INPUT_CLS} />
                  </CampoForm>
                  {parteDrawer.tipo === 'PF' && (
                    <CampoForm label="Estado civil" className="sm:col-span-2"
                      ajuda={(formParte as any).estadoCivilDetalhado ? `Código bruto iSolve: ${(formParte as any).estadoCivilDetalhado}` : undefined}>
                      <select value={(formParte as any).estadoCivilCurado ?? ''}
                        onChange={(e) => setFormParte((f) => ({ ...f, estadoCivilCurado: e.target.value as any }))}
                        className={INPUT_CLS}>
                        {OPCOES_ESTADO_CIVIL.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                      </select>
                    </CampoForm>
                  )}
                </div>

                {/* Marcadores (booleanos) */}
                <div>
                  <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-1.5">Marcadores</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    {CAMPOS_BOOLEANO_PARTE.map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={!!(formParte as any)[key]}
                          onChange={(e) => setFormParte((f) => ({ ...f, [key]: e.target.checked }))}
                          className="rounded border-slate-300" />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Seções detalhadas (grid responsivo) */}
                {SECOES_PARTE.map((secao) => (
                  <div key={secao.titulo}>
                    <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-100 pb-1.5">{secao.titulo}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                      {secao.campos.map((campo) => (
                        <CampoForm key={campo.key} label={campo.label}>
                          <input type={campo.tipo} step={campo.tipo === 'number' ? '0.01' : undefined}
                            value={campo.tipo === 'date' ? String((formParte as any)[campo.key] ?? '').slice(0, 10) : ((formParte as any)[campo.key] ?? '')}
                            onChange={(e) => setFormParte((f) => ({ ...f, [campo.key]: e.target.value }))}
                            className={INPUT_CLS} />
                        </CampoForm>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Contas bancárias (JSON) */}
                <div>
                  <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-1.5">Contas bancárias (JSON)</h4>
                  <textarea rows={5} value={(formParte as any).dadosBancariosCompradorTexto ?? ''}
                    onChange={(e) => setFormParte((f) => ({ ...f, dadosBancariosCompradorTexto: e.target.value }))}
                    placeholder='[{"idBanco":25,"nrAgencia":"0000","nrConta":"00000"}]'
                    className={INPUT_CLS + ' font-mono'} />
                  <p className="text-xs text-slate-400 mt-1">Formato iSolve: lista de objetos com idBanco, nrAgencia e nrConta.</p>
                </div>

                {erroParte && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                    <AlertTriangle size={14} className="shrink-0" />{erroParte}
                  </p>
                )}
                {sucessoParte && (
                  <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="shrink-0" />Dados salvos com sucesso.
                  </p>
                )}

                {/* Write-back CRM → iSolve - confirmação e resultado in-screen (sem alert nativo). */}
                {refletirPendente && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 space-y-2">
                    <p className="text-xs text-blue-800">Dados salvos. Deseja refletir esta alteração no iSolve?</p>
                    <div className="flex gap-2">
                      <button type="button" onClick={refletirAgora} disabled={refletindo}
                        className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors">
                        {refletindo ? 'Replicando…' : 'Refletir no iSolve'}
                      </button>
                      <button type="button" onClick={() => setRefletirPendente(false)} disabled={refletindo}
                        className="text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors">
                        Agora não
                      </button>
                    </div>
                  </div>
                )}

                {reflexoMensagem && (
                  <p className={`text-xs rounded-lg px-3 py-2 border flex items-center gap-1.5 ${
                    reflexoMensagem.tipo === 'sucesso' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                    : reflexoMensagem.tipo === 'erro' ? 'text-red-600 bg-red-50 border-red-200'
                    : 'text-amber-700 bg-amber-50 border-amber-200'
                  }`}>
                    {reflexoMensagem.tipo === 'sucesso' ? <CheckCircle2 size={14} className="shrink-0" /> : reflexoMensagem.tipo === 'erro' ? <XCircle size={14} className="shrink-0" /> : <AlertTriangle size={14} className="shrink-0" />}
                    {reflexoMensagem.texto}
                  </p>
                )}

                {/* SPEC_LAYOUT_UX_V1 (E6) - Contato como entidade: bloco de contatos
                    (leitura + adição manual). Fora de escopo: matriz RACI, régua de
                    comunicação, diagnóstico dos contatos faltantes do Gateway. */}
                <div>
                  <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-1.5">Contatos</h4>
                  <div className="space-y-1.5">
                    {parteDrawer.contatos.length === 0 && (
                      <p className="text-xs text-slate-400">Nenhum contato cadastrado além dos campos acima.</p>
                    )}
                    {parteDrawer.contatos.map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-sm border border-slate-100 rounded-lg px-3 py-2">
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-semibold uppercase shrink-0">{c.tipo}</span>
                        <span className="flex-1 truncate">{c.valor}</span>
                        {c.principal && <span className="text-[10px] text-blue-600 shrink-0">principal</span>}
                        <span className="text-[10px] text-slate-400 shrink-0" title="Origem do dado">{c.origem}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5 pt-2">
                    <select
                      value={novoContato.tipo}
                      onChange={(e) => setNovoContato((s) => ({ ...s, tipo: e.target.value as typeof novoContato.tipo }))}
                      className="text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                    >
                      <option value="telefone">Telefone</option>
                      <option value="email">E-mail</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                    <input
                      type="text"
                      value={novoContato.valor}
                      onChange={(e) => setNovoContato((s) => ({ ...s, valor: e.target.value }))}
                      placeholder="Valor do contato"
                      className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5"
                    />
                    <button
                      type="button"
                      onClick={adicionarContato}
                      disabled={salvandoContato}
                      className="text-xs font-medium text-white bg-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors shrink-0"
                    >
                      {salvandoContato ? 'Salvando…' : 'Adicionar'}
                    </button>
                  </div>
                  {erroContato && <p className="text-xs text-red-500 mt-1">{erroContato}</p>}
                </div>
              </div>

              {/* ── Rodapé fixo (ações) ── */}
              <div className="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-white">
                <button type="button"
                  onClick={() => { setParteDrawer(null); setErroParte(null); setSucessoParte(false); }}
                  className="text-sm font-medium text-slate-600 border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors">
                  Cancelar
                </button>
                <button onClick={salvarParte} disabled={salvandoParte}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-otm-orange hover:bg-otm-orange-dark px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                  <Save size={15} />{salvandoParte ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
